import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../api';
import { useLanguage } from '../../i18n/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import './AiScreening.css';

// AI-assisted screening: proposals with cited evidence, reviewed and approved by the Recruitment Committee.
// There is deliberately no button that rejects or shortlists applicants.

const STATUS_LABELS = {
    MEETS_MANDATORY: 'Meets mandatory', NEEDS_REVIEW: 'Needs review', DOES_NOT_MEET: 'Does not meet',
    COMMITTEE_APPROVED: 'Committee approved', MET: 'Met', NOT_MET: 'Not met', UNKNOWN: 'Unknown',
    SCORED: 'Scored', UNSCORED: 'Unscored',
};
const RUN_LABELS = { pending: 'Queued', running: 'Running', paused: 'Paused', completed: 'Completed', failed: 'Failed', cancelled: 'Cancelled' };

const badgeClass = (s) => `ais-badge ais-${String(s || '').toLowerCase()}`;
const pct = (a, b) => (b ? Math.round((100 * a) / b) : 0);

const AiScreening = () => {
    const { t, language, toggleLanguage } = useLanguage();
    const sw = language === 'sw';
    const { hasPermission, user } = useAuth();
    // screening.view: see results; screening.review: review with reasons; screening.manage: runs, settings, approvals.
    const canManage = hasPermission('screening.manage');
    const canReview = canManage || hasPermission('screening.review');
    const canView = canReview || hasPermission('screening.view');
    const isAdmin = hasPermission('admin.users');
    const [showAccess, setShowAccess] = useState(false);
    const [feedbackJob, setFeedbackJob] = useState(null);

    const [overview, setOverview] = useState(null);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [busy, setBusy] = useState(false);
    const [run, setRun] = useState(null);
    const [outcomeSummary, setOutcomeSummary] = useState(null);
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });
    const [filters, setFilters] = useState({ status: '', application_outcome: '', min_score: '', max_score: '', reviewed: '', search: '', sort: 'eligible', dir: 'desc', work_stage: '', mine: '', high_not_meet: '', eligibility: '', approved: '', fields: '' });
    const [assignment, setAssignment] = useState(null);
    const [showAssign, setShowAssign] = useState(false);
    const [showShortlist, setShowShortlist] = useState(false);
    const [showCutoff, setShowCutoff] = useState(false);
    const [showGeneral, setShowGeneral] = useState(false);
    const [showShortlistApproval, setShowShortlistApproval] = useState(false);
    const [assessment, setAssessment] = useState(null);
    const [criteria, setCriteria] = useState(null);
    const [showSettings, setShowSettings] = useState(false);
    const [pilotSize, setPilotSize] = useState({});

    const loadOverview = useCallback(async () => {
        try {
            const res = await api.aiScreeningOverview();
            setOverview(res.data);
            setError('');
        } catch (e) {
            setError(e.message);
        }
    }, []);

    useEffect(() => { loadOverview(); }, [loadOverview]);

    const latestRows = useRef(0); // ignore responses that arrive after a newer search
    const searchTimer = useRef(null);
    const loadRows = useCallback(async (runId, page = 1, f = filters) => {
        const requestId = ++latestRows.current;
        const res = await api.aiAssessments(runId, { ...f, page, per_page: 50 });
        if (requestId !== latestRows.current) return;
        setRows(res.data || []);
        setPagination(res.pagination || { page: 1, total: 0, total_pages: 1 });
        return res.data || [];
    }, [filters]);

    const loadAssignment = async (runId) => {
        try { const a = (await api.aiAssignments(runId)).data; setAssignment(a); return a; } catch (e) { setAssignment(null); return null; }
    };
    const loadOutcomeSummary = async (runId) => {
        try {
            const summary = (await api.aiApplicationOutcomeReviews(runId)).data;
            setOutcomeSummary(summary);
            return summary;
        } catch (e) {
            setOutcomeSummary(null);
            setError(e.message);
            return null;
        }
    };
    const openRun = async (runId) => {
        try {
            const res = await api.aiRunProgress(runId);
            setRun(res.data);
            setOutcomeSummary(null);
            const a = await loadAssignment(runId);
            const myAssignments = (a?.by_reviewer || []).find((r) => r.reviewer_id === Number(user?.id));
            const mineCount = myAssignments?.assigned || 0;
            const second = Number(myAssignments?.second_assigned) > 0;
            const next = { ...filters, application_outcome: '', mine: mineCount > 0 ? '1' : '', work_stage: second ? 'second' : '', status: '', reviewed: '', eligibility: '', approved: '', min_score: '', max_score: '', high_not_meet: '', fields: '', search: '' };
            setFilters(next);
            await loadRows(runId, 1, next);
            await loadOutcomeSummary(runId);
        } catch (e) {
            setError(e.message);
        }
    };

    // Refresh progress while a run is working.
    useEffect(() => {
        if (!run || !['pending', 'running'].includes(run.status)) return undefined;
        const timer = setInterval(async () => {
            try {
                const res = await api.aiRunProgress(run.id);
                setRun(res.data);
                await loadRows(run.id, pagination.page);
            } catch (e) { /* keep last view */ }
        }, 15000);
        return () => clearInterval(timer);
    }, [run, pagination.page, loadRows]);

    const act = async (fn, okMessage) => {
        setBusy(true);
        setNotice('');
        try {
            const res = await fn();
            setNotice(res?.message || okMessage || t('Done.'));
            await loadOverview();
            return res;
        } catch (e) {
            setError(e.message);
            return null;
        } finally {
            setBusy(false);
        }
    };

    const startRun = async (job, version, pilot) => {
        const msg = pilot
            ? t('Start a pilot on {n} randomly chosen applications? Pilot results are for checking accuracy only.').replace('{n}', pilotSize[job.id] || 50)
            : t('Start AI screening of all {n} applications for this post with the approved criteria?').replace('{n}', job.applications);
        if (!window.confirm(msg)) return;
        const res = await act(() => api.aiCreateRun({ job_posting_id: job.id, criteria_version_id: version.id, pilot_size: pilot ? Number(pilotSize[job.id] || 50) : null }));
        if (res?.data) openRun(res.data.id);
    };

    const runAction = async (action) => {
        const res = await act(() => api.aiRunAction(run.id, action));
        if (res?.data) setRun({ ...run, ...res.data });
    };
    const downloadApplicantLists = async () => {
        try { await api.aiGeneralReportApplicantsPdf(sw ? 'sw' : 'en'); }
        catch (e) { setError(t(e.message)); }
    };

    const downloadShortlistContacts = async () => {
        try { await api.aiShortlistContactsPdf(sw ? 'sw' : 'en'); }
        catch (e) { setError(t(e.message)); }
    };

    const applyFilters = (patch) => {
        const next = { ...filters, ...patch };
        setFilters(next);
        if (!run) return;
        clearTimeout(searchTimer.current);
        // Typing in the search box: wait until the user pauses; other filters apply at once.
        if ('search' in patch || 'min_score' in patch || 'max_score' in patch) searchTimer.current = setTimeout(() => loadRows(run.id, 1, next), 450);
        else loadRows(run.id, 1, next);
    };

    // Where the open application sits in the list, so "Next" still works after a save removes it from a filtered list.
    const position = useRef({ page: 1, index: -1 });
    const openAssessment = async (id, pos = null) => {
        try {
            const res = await api.aiAssessment(id);
            position.current = pos || { page: pagination.page, index: rows.findIndex((r) => r.id === id) };
            setAssessment(res.data);
            return true;
        } catch (e) {
            setError(e.message);
            return false;
        }
    };
    // Opens the previous/next application in the current (filtered) list, crossing list pages.
    const goTo = async (dir, list = rows) => {
        if (!run || !assessment) return false;
        const { page, index } = position.current;
        const at = list.findIndex((r) => r.id === assessment.id);
        const i = at >= 0 ? at + dir : (dir > 0 ? index : index - 1);
        if (i >= 0 && i < list.length) return openAssessment(list[i].id, { page, index: i });
        if (dir > 0 && page < pagination.total_pages) {
            const next = await loadRows(run.id, page + 1);
            if (next.length) return openAssessment(next[0].id, { page: page + 1, index: 0 });
        }
        if (dir < 0 && page > 1) {
            const prev = await loadRows(run.id, page - 1);
            if (prev.length) return openAssessment(prev[prev.length - 1].id, { page: page - 1, index: prev.length - 1 });
        }
        return false;
    };
    const afterReview = async (d, autoNext) => {
        setAssessment(d);
        if (!run) return;
        api.aiRunProgress(run.id).then((res) => setRun(res.data)).catch(() => {});
        const list = await loadRows(run.id, position.current.page);
        if (autoNext && !(await goTo(1, list))) setNotice(t('Saved. That was the last application in this list.'));
    };
    const approveAllOutcomes = async () => {
        if (!run || !outcomeSummary?.ready || outcomeSummary.pending < 1) return;
        const message = t('Accept all {n} pending applicant outcomes for this vacancy? This includes both shortlisted and not-shortlisted applicants. Returned outcomes are not included.').replace('{n}', outcomeSummary.pending);
        if (!window.confirm(message)) return;
        setBusy(true);
        setError('');
        try {
            const acceptedNow = outcomeSummary.pending;
            const result = await api.aiApprovePendingApplicationOutcomes(run.id);
            setOutcomeSummary(result.data);
            await loadRows(run.id, pagination.page);
            if (assessment) {
                const refreshed = await api.aiAssessment(assessment.id);
                setAssessment(refreshed.data);
            }
            setNotice(t('Accepted {n} pending applicant outcomes.').replace('{n}', String(acceptedNow)));
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    if (!canView) {
        return <div className="ai-screening-page"><div className="ais-empty">{t('You do not have access to recruitment screening.')}</div></div>;
    }

    const settings = overview?.settings;

    return (
        <div className="ai-screening-page">
            <header className="ais-hero">
                <div>
                    <span className="ais-kicker">{t('RECRUITMENT · DECISION SUPPORT')}</span>
                    <h1>{t('AI Screening')}</h1>
                    <p>{t('AI reads each application against the advertised criteria and cites its evidence. The Committee reviews, corrects and approves every result.')}</p>
                </div>
                <div className="ais-hero-actions">
                    <button type="button" className="ais-btn ais-btn-light ais-lang" onClick={toggleLanguage} aria-label={sw ? 'Switch to English' : 'Badilisha kwenda Kiswahili'}>
                        <span className={sw ? '' : 'on'}>EN</span><span className={sw ? 'on' : ''}>SW</span>
                    </button>
                    <button type="button" className="ais-btn ais-btn-light" onClick={() => setShowGeneral(true)}>📊 {t('General report')}</button>
                    <button type="button" className="ais-btn ais-btn-light" onClick={() => setShowShortlistApproval(true)}>{t('Shortlist approval')}</button>
                    {isAdmin && <button type="button" className="ais-btn ais-btn-light" onClick={() => setShowAccess(true)}>{t('Who can see')}</button>}
                    {canManage && <button type="button" className="ais-btn ais-btn-light" onClick={() => setShowSettings(true)}>{t('Settings')}</button>}
                </div>
            </header>

            {user?.prelaunch_scope === 'screening' && <div className="ais-alert ais-alert-ok">{t('The other parts of the portal open when the portal launches. Until then you can use your profile and AI Screening.')}</div>}
            {error && <div className="ais-alert ais-alert-error" role="alert">{error} <button type="button" onClick={() => setError('')} aria-label={t('Close')}>✕</button></div>}
            {notice && <div className="ais-alert ais-alert-ok">{notice} <button type="button" onClick={() => setNotice('')} aria-label={t('Close')}>✕</button></div>}
            {settings?.blocker && <div className="ais-alert ais-alert-warn">{t(settings.blocker)}</div>}

            {!overview && !error && <div className="ais-empty">{t('Loading...')}</div>}

            {overview && !run && (
                <>
                    {overview.snapshot && (
                        <p className="ais-note">
                            {t('Frozen record')}: {overview.snapshot.application_count} {t('applications')}, {overview.snapshot.document_count} {t('documents')} · {t('deadline')} {overview.snapshot.deadline_at} EAT
                        </p>
                    )}
                    <div className="ais-vacancies">
                        {overview.vacancies.map((job) => {
                            const ex = job.extraction || {};
                            const total = Object.values(ex).reduce((a, b) => a + b, 0);
                            const done = (ex.done || 0) + (ex.truncated || 0);
                            const latest = job.criteria_versions[0];
                            const approved = job.criteria_versions.find((v) => v.status === 'approved');
                            const active = job.runs.find((r) => ['pending', 'running', 'paused'].includes(r.status));
                            return (
                                <section key={job.id} className="ais-card">
                                    <div className="ais-card-head">
                                        <div>
                                            <small>{job.reference_number}</small>
                                            <h2>{t(job.title)}</h2>
                                        </div>
                                        <b className="ais-count">{job.applications}<small>{t('applications')}</small></b>
                                    </div>
                                    <div className="ais-progress" title={t('Text read from documents (OCR for scans)')}>
                                        <div style={{ width: `${pct(done, total)}%` }} />
                                    </div>
                                    <p className="ais-small">{t('Documents read')}: {done} / {total}{ex.failed ? ` · ${ex.failed} ${t('unreadable')}` : ''}</p>

                                    <div className="ais-row">
                                        <span>{t('Criteria')}:</span>
                                        {latest ? (
                                            <>
                                                <span className={badgeClass(latest.status)}>v{latest.version} {t(latest.status)}</span>
                                                {latest.status === 'draft' && latest.approved_by && <span className="ais-small">{t('1 of 2 approvals')}</span>}
                                                <button type="button" className="ais-link" onClick={async () => setCriteria((await api.aiCriteria(latest.id)).data)}>{t('View')}</button>
                                            </>
                                        ) : <span className="ais-small">{t('not loaded')}</span>}
                                    </div>

                                    {job.runs.length > 0 && (
                                        <ul className="ais-runs">
                                            {job.runs.slice(0, 4).map((r) => (
                                                <li key={r.id}>
                                                    <button type="button" className="ais-link" onClick={() => openRun(r.id)}>
                                                        {t('Run')} {r.id}{Number(r.is_pilot) ? ` · ${t('pilot')}` : ''}
                                                    </button>
                                                    <span className={badgeClass(r.status)}>{t(RUN_LABELS[r.status] || r.status)}</span>
                                                    <span className="ais-small">{r.processed}/{r.total}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}

                                    {canManage && (
                                        <button type="button" className="ais-link ais-feedback-link" onClick={() => setFeedbackJob(job)}>{t('Feedback to applicants')}</button>
                                    )}
                                    {canManage && latest && !active && (
                                        <div className="ais-actions">
                                            <label className="ais-pilot">
                                                {t('Pilot size')}
                                                <input type="number" min="1" max="100" value={pilotSize[job.id] ?? 50} onChange={(e) => setPilotSize({ ...pilotSize, [job.id]: e.target.value })} />
                                            </label>
                                            <button type="button" className="ais-btn" disabled={busy || !!settings?.blocker} onClick={() => startRun(job, latest, true)}>{t('Start pilot')}</button>
                                            <button type="button" className="ais-btn ais-btn-dark" disabled={busy || !approved || !!settings?.blocker} title={approved ? '' : t('Needs committee-approved criteria')} onClick={() => startRun(job, approved, false)}>{t('Screen all')}</button>
                                            {!approved && (
                                                <p className="ais-hint">
                                                    {latest.approved_by
                                                        ? t('"Screen all" opens after a second committee member approves the criteria (View → Give second approval).')
                                                        : t('"Screen all" opens after two committee members approve the criteria (View → Approve criteria).')}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </section>
                            );
                        })}
                    </div>
                </>
            )}

            {run && (
                <RunView
                    t={t} sw={sw} run={run} rows={rows} pagination={pagination} filters={filters} canManage={canManage} isAdmin={isAdmin}
                    showOutcomeTabs={!!overview?.can_view_applicant_lists} isReportApprover={!!overview?.is_report_approver} busy={busy}
                    outcomeSummary={outcomeSummary} onApproveAll={approveAllOutcomes}
                    onApplicantPdf={downloadApplicantLists} onContactsPdf={downloadShortlistContacts}
                    assignment={assignment} me={Number(user?.id)} onAssign={() => setShowAssign(true)} onShortlist={() => setShowShortlist(true)}
                    onCutoff={isAdmin && !Number(run.is_pilot) ? () => setShowCutoff(true) : null}
                    onBack={() => { setRun(null); loadOverview(); }}
                    onFilter={applyFilters} onPage={(p) => loadRows(run.id, p)} onOpen={openAssessment} onAction={runAction}
                    onExport={() => api.aiExport(run.id, filters).catch((e) => setError(e.message))}
                    onReport={async (format, layout) => {
                        setBusy(true); setNotice(t(format === 'pdf' ? 'Preparing the PDF report… this can take up to a minute for a large post.' : 'Preparing the Excel report…'));
                        try { await api.aiReport(run.id, format, { ...filters, lang: sw ? 'sw' : 'en', ...(layout ? { layout } : {}) }); setNotice(''); } catch (e) { setNotice(''); setError(t(e.message)); } finally { setBusy(false); }
                    }}
                />
            )}

            {assessment && (
                <AssessmentModal
                    key={assessment.id}
                    t={t} sw={sw} data={assessment} canManage={canManage} canReview={canReview}
                    nav={run ? { onPrev: () => goTo(-1), onNext: () => goTo(1), label: position.current.index >= 0 ? `${(position.current.page - 1) * 50 + position.current.index + 1} / ${pagination.total}` : '' } : null}
                    onReviewed={afterReview}
                    onClose={() => setAssessment(null)}
                    onWithdrawn={(m) => { setAssessment(null); setNotice(t(m)); if (run) { loadRows(run.id, pagination.page); loadAssignment(run.id); } }}
                    onSaved={(d) => { setAssessment(d); if (run) { loadRows(run.id, pagination.page); loadOutcomeSummary(run.id); } }}
                    onError={setError}
                />
            )}
            {criteria && <CriteriaModal t={t} sw={sw} data={criteria} canManage={canManage} onClose={() => { setCriteria(null); loadOverview(); }} onApproved={() => { setCriteria(null); loadOverview(); }} onError={setError} />}
            {showShortlistApproval && <ShortlistApprovalModal t={t} onClose={() => setShowShortlistApproval(false)} />}
            {showAccess && <AccessModal t={t} onClose={() => setShowAccess(false)} />}
            {showGeneral && <GeneralReportModal t={t} sw={sw} onClose={() => setShowGeneral(false)} />}
            {showAssign && run && <AssignModal t={t} run={run} onClose={() => { setShowAssign(false); loadAssignment(run.id); loadRows(run.id, 1); }} />}
            {showCutoff && run && (
                <CutoffModal t={t} run={run} onClose={async (changed) => {
                    setShowCutoff(false);
                    if (changed) { try { setRun((await api.aiRunProgress(run.id)).data); } catch (e) { /* keep */ } loadRows(run.id, 1); }
                }} />
            )}
            {showShortlist && run && <ShortlistModal t={t} run={run} onClose={() => { setShowShortlist(false); loadRows(run.id, pagination.page); }} />}
            {feedbackJob && <FeedbackModal t={t} job={feedbackJob} isAdmin={isAdmin} onClose={() => setFeedbackJob(null)} />}
            {showSettings && <SettingsModal t={t} onClose={() => setShowSettings(false)} onSaved={() => { setShowSettings(false); loadOverview(); }} />}
        </div>
    );
};

const CommitteeStatus = ({ t, r }) => {
    if (r.status === 'COMMITTEE_APPROVED') return <span className={badgeClass('COMMITTEE_APPROVED')}>{t('Approved')}: {t(STATUS_LABELS[r.reviewed_eligibility_status])}</span>;
    if (r.reviewed_eligibility_status) return <span className={badgeClass(r.reviewed_eligibility_status)}>{t('Reviewed')}: {t(STATUS_LABELS[r.reviewed_eligibility_status])}</span>;
    return <span className="ais-small">{t('Not reviewed')}</span>;
};

// Scores rank only applicants who meet the mandatory requirements; for the rest they are shown faded.
const Score = ({ r, t }) => {
    const out = (r.reviewed_eligibility_status || r.eligibility_status) === 'DOES_NOT_MEET';
    const body = r.reviewed_score !== null && r.reviewed_score !== undefined
        ? <><b>{Number(r.reviewed_score)}</b> <s className="ais-small">{Number(r.proposed_score)}</s></>
        : <b>{r.proposed_score !== null ? Number(r.proposed_score) : '—'}</b>;
    if (!out) return body;
    // No number in the list for someone who did not pass: it reads as a pass. The score stays in the tooltip and the window.
    const score = r.reviewed_score ?? r.proposed_score;
    return (
        <span className="ais-score-out" title={t ? `${t('Does not meet a mandatory requirement: this score is not used for ranking')}${score !== null && score !== undefined ? ` (${Number(score)})` : ''}` : ''}>
            — <small>{t ? t('Not passed') : ''}</small>
        </span>
    );
};

const ShortlistBadge = ({ t, status }) => {
    if (status === 'shortlisted') return <span className="ais-badge ais-committee_approved">{t('Shortlisted')}</span>;
    if (status === 'not_shortlisted') return <span className="ais-badge">{t('Not shortlisted')}</span>;
    return null;
};

// Managers share a run's applications among Committee members (evenly, 1-3 reviewers per application).
const AssignModal = ({ t, run, onClose }) => {
    const [data, setData] = useState(null);
    const [chosen, setChosen] = useState([]);
    const [per, setPer] = useState(1);
    const [err, setErr] = useState('');
    const [msg, setMsg] = useState('');
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null);
    const [reset, setReset] = useState(false);
    const [stage, setStage] = useState('review');
    useEffect(() => {
        api.aiAssignments(run.id).then((r) => { setData(r.data); setChosen(r.data.reviewers.map((x) => x.id)); }).catch((e) => setErr(e.message));
    }, [run.id]);
    // Shares each chosen member would get, worked out by the server with the same rule as "Assign" (nothing saved).
    const chosenKey = chosen.slice().sort((a, b) => a - b).join(',');
    useEffect(() => {
        if (!data || !chosen.length || per > chosen.length) { setPreview(null); return undefined; }
        let live = true;
        const timer = setTimeout(() => {
            api.aiSaveAssignments(run.id, { action: 'preview', reviewer_ids: chosen, per_application: per, reset: reset ? 1 : 0, stage })
                .then((r) => live && setPreview(r.data)).catch(() => live && setPreview(null));
        }, 250);
        return () => { live = false; clearTimeout(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [run.id, chosenKey, per, data, reset, stage]);
    const act = async (body) => {
        setBusy(true); setErr(''); setMsg('');
        try { const r = await api.aiSaveAssignments(run.id, { ...body, stage }); setData(r.data); setMsg(r.message); } catch (e) { setErr(e.message); } finally { setBusy(false); }
    };
    const load = Object.fromEntries((data?.by_reviewer || []).map((r) => [r.reviewer_id, r]));
    const share = Object.fromEntries((preview?.preview || []).map((p) => [p.reviewer_id, p]));
    return (
        <div className="ais-modal-overlay" role="dialog" aria-modal="true" aria-label={t('Assign to members')}>
            <div className="ais-modal ais-modal-sm">
                <div className="ais-modal-head"><h2>{t('Assign to members')}</h2><button type="button" className="ais-close" onClick={onClose} aria-label={t('Close')}>×</button></div>
                {err && <div className="ais-alert ais-alert-error">{err}</div>}
                {msg && <div className="ais-alert ais-alert-ok">{msg}</div>}
                {!data && !err && <div className="ais-empty">{t('Loading...')}</div>}
                {data && (
                    <>
                        <p className="ais-small">{data.total} {t('applications')} · {data.assigned_applications} {t('assigned')} · {data.unassigned} {t('not assigned')} · {data.reviewed} {t('reviewed')}</p>
                        {!data.reviewers.length && <div className="ais-alert ais-alert-warn">{t('No member can review yet. Give members "Can review" in Who can see.')}</div>}
                        <label className="ais-block-label">{t('Review stage')}
                            <select value={stage} onChange={(e) => { setStage(e.target.value); setPreview(null); setReset(false); }}>
                                <option value="review">{t('First review')}</option>
                                <option value="second_approval">{t('Second review — Meets mandatory only')}</option>
                            </select>
                        </label>
                        <p className="ais-small">{t('Second review includes only applications confirmed as Meets mandatory in the first review. Members who previously worked on an application are excluded across all runs.')}</p>
                        <ul className="ais-access">
                            {data.reviewers.map((r) => (
                                <li key={r.id}>
                                    <label className="ais-check"><input type="checkbox" checked={chosen.includes(r.id)} onChange={(e) => setChosen(e.target.checked ? [...chosen, r.id] : chosen.filter((x) => x !== r.id))} />
                                        <span className="ais-access-who"><b>{r.name}</b><small>{r.email}{r.manager ? ` · ${t('Can manage')}` : ''}</small></span></label>
                                    <span className="ais-assign-count">
                                        {chosen.includes(r.id) && share[r.id] && (
                                            <b className={share[r.id].new ? '' : 'ais-muted'}>{share[r.id].new ? `+${share[r.id].new}` : '+0'}<small> · {t('total')} {share[r.id].total}</small></b>
                                        )}
                                        <span className="ais-small">{load[r.id] ? `${load[r.id].reviewed} / ${load[r.id].assigned} ${t('reviewed')}` : t('none assigned')}</span>
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <div className="ais-form-row">
                            <label>{t('Reviewers per application')}
                                <select value={per} onChange={(e) => setPer(Number(e.target.value))}><option value={1}>1</option><option value={2}>2</option><option value={3}>3</option></select>
                            </label>
                            <span className="ais-small">{t('Applications are shared evenly. Members who already worked on an application, including conflicts and previous runs, cannot be assigned it again.')}</span>
                        </div>
                        <label className="ais-check ais-reset-check"><input type="checkbox" checked={reset} onChange={(e) => setReset(e.target.checked)} /> <span><b>{t('Re-share from scratch')}</b> – {t('replace pending assignments for the selected review stage. Completed work is kept as history.')}</span></label>
                        {per > chosen.length && chosen.length > 0 && <div className="ais-alert ais-alert-warn">{t('Choose more members than reviewers per application.')}</div>}
                        {preview && (
                            <div className="ais-alert ais-alert-ok ais-assign-preview">
                                {preview.added
                                    ? t('"Assign" will give {n} new review(s) to {m} member(s): about {each} each.').replace('{n}', preview.added).replace('{m}', chosen.length).replace('{each}', Math.round(preview.added / chosen.length))
                                    : t('Every application already has enough reviewers; "Assign" will add nothing.')}
                                {preview.could_not_fill > 0 && ` ${t('{k} application(s) cannot get enough reviewers (prior work or conflicts).').replace('{k}', preview.could_not_fill)}`}
                                {preview.reset && <small>{t('{r} pending assignment(s) will be replaced.').replace('{r}', preview.replaced)}</small>}
                                {run.status !== 'completed' && <small>{t('Counts cover the {n} applications analysed so far; run "Assign" again when the run finishes to share the rest.').replace('{n}', preview.open_applications)}</small>}
                            </div>
                        )}
                        <div className="ais-actions">
                            <button type="button" className="ais-btn ais-btn-dark" disabled={busy || !chosen.length || per > chosen.length}
                                onClick={() => (!reset || window.confirm(t('Replace pending assignments for the selected review stage? Completed work is kept.'))) && act({ action: reset ? 'reset' : 'distribute', reviewer_ids: chosen, per_application: per })}>
                                {reset ? t('Re-share') : t('Assign')}
                            </button>
                            <button type="button" className="ais-btn ais-btn-danger" disabled={busy || !data.assigned_applications} onClick={() => window.confirm(t('Remove all assignments of this run?')) && act({ action: 'clear' })}>{t('Clear assignments')}</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// Admin: record "does not meet – score below the Committee cut-off" for every not-yet-reviewed application at or below
// a score. Shows the count first; a reason (Committee decision / minutes) is required; second approval still applies.
const ShortlistApprovalModal = ({ t, onClose }) => {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [notes, setNotes] = useState({});
    const [err, setErr] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    useEffect(() => { api.aiShortlistApprovals().then((r) => setData(r.data)).catch((e) => setErr(e.message)); }, []);
    const act = async (job, action) => {
        if (!window.confirm(t(action === 'submit' ? 'Submit this shortlist to the designated leader? No applicant notification will be sent.' : action === 'approve' ? 'Approve this shortlist for the interview stage? No applicant notification will be sent.' : 'Return this shortlist to the Committee for correction?') + (action === 'return' ? '' : '\n\n' + t('Your uploaded signature will be added to this document.')))) return;
        setBusy(true); setErr(''); setMsg('');
        try {
            const r = await api.aiShortlistApprovalAction(job.id, action, notes[job.id] || '', job.workflow.approval?.fingerprint || '');
            setData(r.data); setNotes({ ...notes, [job.id]: '' }); setMsg(t('Shortlist approval updated. Applicant notifications remain paused.'));
        } catch (e) { setErr(t(e.message)); } finally { setBusy(false); }
    };
    return <div className="ais-modal-overlay" role="dialog" aria-modal="true" aria-label={t('Shortlist approval')}>
        <div className="ais-modal">
            <div className="ais-modal-head"><h2>{t('Shortlist approval')}</h2><button type="button" className="ais-close" disabled={busy} onClick={onClose} aria-label={t('Close')}>×</button></div>
            {err && <div className="ais-alert ais-alert-error">{err}</div>}
            {msg && <div className="ais-alert ais-alert-ok">{msg}</div>}
            {!data && !err && <div className="ais-empty">{t('Loading...')}</div>}
            {data && <>
                <p>{t('Designated leader')}: <b>{data.approver_name || '—'}</b></p>
                {(data.can_submit || data.can_decide) && !data.my_signature?.has_signature && <p className="ais-alert ais-alert-warn">{t('Upload your signature in My Profile before signing or approving this document.')} <a href={user?.role === 'member' ? '/profile' : '/staff-profile'} target="_blank" rel="noreferrer">{t('My Profile')}</a></p>}
                {data.submitter_name && <p>{t('Secretary submitting the shortlist')}: <b>{data.submitter_name}</b></p>}
                <p className="ais-small">{t('Process: Committee shortlist → Submit for leadership approval → Leader approves or returns → Interviewed → Accepted or Rejected. Approval is separate from the hiring decision.')}</p>
                {data.notifications_paused !== false && <div className="ais-alert ais-alert-warn">{t('Applicant notifications are paused. Approval does not release feedback. Drafting and internal previews remain available.')}</div>}
                {data.jobs.map((job) => {
                    const w = job.workflow;
                    const submitted = w.status === 'pending' && w.valid && !!w.approval?.submitted_signature;
                    const done = w.status === 'approved' && w.valid;
                    return <section key={job.id} className="ais-gr-section">
                        <h3>{t(job.title)} <span className={badgeClass(done ? 'approved' : w.status === 'returned' ? 'needs_review' : 'draft')}>{t(done ? 'Approved shortlist' : submitted ? 'Awaiting leadership approval' : w.status === 'returned' ? 'Returned for correction' : w.approval ? 'Changed — submit again' : 'Awaiting submission to the chairperson')}</span></h3>
                        <p className="ais-small">{w.candidates.length} {t('Shortlisted')} {w.approval?.submitted_at && <> · {t('Submitted')}: {w.approval.submitted_at}</>}{w.approval?.reviewed_at && <> · {t('Decision')}: {w.approval.reviewed_at}</>}</p>
                        {w.approval?.reference && <p>{t('Committee decision / minutes reference (required)')}: {w.approval.reference}</p>}
                        {w.approval?.decision_note && <p className="ais-gr-text">{w.approval.decision_note}</p>}
                        {w.approval && !w.valid && <p className="ais-alert ais-alert-warn">{t('The shortlist or assessment changed. Submit the updated shortlist again.')}</p>}
                        {w.ready && !submitted && !done && <p className="ais-alert ais-alert-info">{t(data.can_submit ? 'Enter the Committee minutes / submission reference below, then submit the shortlist to the chairperson for final approval.' : 'The secretary or another screening manager must submit this shortlist first. The chairperson can then give final approval.')}</p>}
                        {!w.ready && w.candidates.length > 0 && <p className="ais-alert ais-alert-warn">{t('Every shortlisted applicant must have a completed second review and meet mandatory requirements.')}</p>}
                        <details><summary>{t('View shortlisted candidates')} ({w.candidates.length})</summary>
                            <div className="ais-table-wrap"><table className="ais-table"><thead><tr><th>{t('Application')}</th><th>{t('Name')}</th><th>{t('Score /100')}</th></tr></thead><tbody>{w.candidates.map((c) => <tr key={c.application_id}><td>{c.application_number}</td><td>{c.applicant_name}</td><td>{c.score ?? '—'}</td></tr>)}</tbody></table></div>
                        </details>
                        {((data.can_submit && !submitted && !done) || (data.can_decide && submitted)) && <label className="ais-block-label">{t('Submission reference / decision note')}
                            <textarea rows="3" maxLength="2000" disabled={busy} value={notes[job.id] || ''} onChange={(e) => setNotes({ ...notes, [job.id]: e.target.value })} />
                        </label>}
                        <div className="ais-actions">
                            {done && w.approval?.signed_document && <button type="button" className="ais-btn" disabled={busy} onClick={() => api.shortlistApprovalPdf(job.id).catch((e) => setErr(t(e.message)))}>{t('Download signed PDF')}</button>}
                            {data.can_submit && !submitted && !done && <button type="button" className="ais-btn ais-btn-dark" disabled={busy || !w.ready || !data.approver_active || !data.my_signature?.has_signature || !(notes[job.id] || '').trim()} onClick={() => act(job, 'submit')}>{t('Submit for leadership approval')}</button>}
                            {data.can_decide && submitted && <>
                                <button type="button" className="ais-btn ais-btn-ok" disabled={busy || !data.my_signature?.has_signature} onClick={() => act(job, 'approve')}>{t('Approve shortlist')}</button>
                                <button type="button" className="ais-btn" disabled={busy || !(notes[job.id] || '').trim()} onClick={() => act(job, 'return')}>{t('Return to Committee')}</button>
                            </>}
                        </div>
                    </section>;
                })}
            </>}
        </div>
    </div>;
};

const CutoffModal = ({ t, run, onClose }) => {
    const [max, setMax] = useState('40');
    const [reason, setReason] = useState('');
    const [withReviewed, setWithReviewed] = useState(false);
    const [preview, setPreview] = useState(null);
    const [err, setErr] = useState('');
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState('');
    useEffect(() => {
        if (max === '' || Number.isNaN(Number(max))) { setPreview(null); return undefined; }
        let live = true;
        const timer = setTimeout(() => {
            api.aiScoreCutoff(run.id, { max_score: Number(max), dry_run: 1, include_reviewed: withReviewed ? 1 : 0 }).then((r) => live && (setPreview(r.data), setErr(''))).catch((e) => live && setErr(e.message));
        }, 300);
        return () => { live = false; clearTimeout(timer); };
    }, [run.id, max, withReviewed]);
    const apply = async () => {
        if (!window.confirm(t('Record {n} applications as "Does not meet" (score {max} or below)? Applications already reviewed by the Committee are not changed.').replace('{n}', preview?.to_mark ?? 0).replace('{max}', max))) return;
        setBusy(true); setErr('');
        try { const r = await api.aiScoreCutoff(run.id, { max_score: Number(max), reason, include_reviewed: withReviewed ? 1 : 0 }); setDone(t('Recorded {n} applications as "Does not meet". Each still needs second approval.').replace('{n}', r.data?.to_mark ?? 0)); setPreview(null); } catch (e) { setErr(e.message); } finally { setBusy(false); }
    };
    return (
        <div className="ais-modal-overlay" role="dialog" aria-modal="true" aria-label={t('Score cut-off')}>
            <div className="ais-modal ais-modal-sm">
                <div className="ais-modal-head"><h2>{t('Score cut-off')}</h2><button type="button" className="ais-close" onClick={() => onClose(!!done)} aria-label={t('Close')}>×</button></div>
                {err && <div className="ais-alert ais-alert-error">{err}</div>}
                {done && <div className="ais-alert ais-alert-ok">{done}</div>}
                <p className="ais-small">{t('Applications not yet reviewed by the Committee whose score is at or below this number are recorded as "Does not meet – score below the Committee cut-off". Applications already reviewed are left as they are. Each still needs second approval.')}</p>
                <div className="ais-form-row">
                    <label>{t('Highest score below the cut-off')}<input type="number" min="0" max="99" value={max} disabled={!!done} onChange={(e) => setMax(e.target.value)} /></label>
                </div>
                <label className="ais-check"><input type="checkbox" checked={withReviewed} disabled={!!done} onChange={(e) => setWithReviewed(e.target.checked)} /> {t('Also change applications already reviewed by a member (not yet approved)')}</label>
                {preview && (
                    <div className="ais-alert ais-alert-warn">
                        <b>{preview.to_mark}</b> {t('applications will be recorded as "Does not meet"')} ({t('score')} 0–{max}).
                        {preview.reviewed_changed > 0 && <><br />{t('including')} <b>{preview.reviewed_changed}</b> {t('already reviewed by a member (their decision is overridden)')}.</>}
                        <br />{preview.already_reviewed_kept} {t('already reviewed by the Committee – not changed')}.
                        {!withReviewed && preview.reviewed_not_does_not_meet > 0 && <><br />{preview.reviewed_not_does_not_meet} {t('reviewed applications in this range are not "Does not meet"; tick the box above to include them.')}</>}
                        {preview.ai_meets_among_them > 0 && <><br />{preview.ai_meets_among_them} {t('of them the AI found to meet every mandatory requirement')}.</>}
                    </div>
                )}
                <label className="ais-block-label">{t('Committee decision / minutes reference (required)')}
                    <textarea rows="3" value={reason} disabled={!!done} placeholder={t('e.g. Committee meeting 21/10/2026: applications scoring 40 or below are not shortlisted.')} onChange={(e) => setReason(e.target.value)} />
                </label>
                <div className="ais-actions">
                    {!done && <button type="button" className="ais-btn ais-btn-danger" disabled={busy || !preview?.to_mark || reason.trim().length < 5} onClick={apply}>{busy ? <span className="ais-spinner" aria-hidden="true" /> : null}{t('Record as "Does not meet"')}</button>}
                    <button type="button" className="ais-btn" onClick={() => onClose(!!done)}>{t('Close')}</button>
                </div>
            </div>
        </div>
    );
};

// After the Committee meeting: managers record who is shortlisted. Only Committee-approved applicants who meet every
// mandatory requirement can be selected; everyone else approved in the run becomes "not shortlisted".
const ShortlistModal = ({ t, run, onClose }) => {
    const [data, setData] = useState(null);
    const [picked, setPicked] = useState([]);
    const [topN, setTopN] = useState('');
    const [minScore, setMinScore] = useState('');
    const [minutes, setMinutes] = useState('');
    const [note, setNote] = useState('');
    const [err, setErr] = useState('');
    const [msg, setMsg] = useState('');
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        api.aiShortlist(run.id).then((r) => { setData(r.data); setPicked(r.data.candidates.filter((c) => c.application_status === 'shortlisted').map((c) => Number(c.application_id))); }).catch((e) => setErr(e.message));
    }, [run.id]);
    const eligible = (data?.candidates || []).filter((c) => c.eligibility === 'MEETS_MANDATORY');
    const pickTop = () => setPicked(eligible.slice(0, Math.max(0, Number(topN) || 0)).map((c) => Number(c.application_id)));
    // Committee cut-off: everyone who meets every mandatory requirement and reaches the minimum score.
    const pickMin = () => setPicked(eligible.filter((c) => c.score !== null && Number(c.score) >= Number(minScore)).map((c) => Number(c.application_id)));
    const reachMin = minScore === '' ? null : eligible.filter((c) => c.score !== null && Number(c.score) >= Number(minScore)).length;
    const save = async () => {
        if (!window.confirm(t('Record {n} as shortlisted and the other {m} approved applicants as not shortlisted?').replace('{n}', picked.length).replace('{m}', data.candidates.length - picked.length))) return;
        setBusy(true); setErr(''); setMsg('');
        try { const r = await api.aiSaveShortlist(run.id, { application_ids: picked, minutes_reference: minutes, note }); setData(r.data); setMsg(r.message); } catch (e) { setErr(e.message); } finally { setBusy(false); }
    };
    return (
        <div className="ais-modal-overlay" role="dialog" aria-modal="true" aria-label={t('Record shortlist')}>
            <div className="ais-modal">
                <div className="ais-modal-head"><div><small>{data?.run?.title}</small><h2>{t('Record shortlist')}</h2></div><button type="button" className="ais-close" onClick={onClose} aria-label={t('Close')}>×</button></div>
                {err && <div className="ais-alert ais-alert-error">{err}</div>}
                {msg && <div className="ais-alert ais-alert-ok">{msg}</div>}
                {!data && !err && <div className="ais-empty">{t('Loading...')}</div>}
                {data && !data.ready && <div className="ais-alert ais-alert-warn">{t('A shortlist can only be recorded from a full screening run with Committee-approved criteria. Pilot runs are for checking accuracy only.')}</div>}
                {data && data.ready && (
                    <>
                        <p className="ais-small">{t('Record the shortlist the Committee agreed in its meeting. Applicants see only "Recruitment Committee decision"; the minutes reference is kept in the audit trail.')}</p>
                        {data.not_yet_approved > 0 && <div className="ais-alert ais-alert-warn">{data.not_yet_approved} {t('applications in this run are not yet Committee-approved and are left unchanged.')}</div>}
                        {data.last_decision && <p className="ais-small">{t('Last recorded')}: {data.last_decision.decided_at} · {data.last_decision.minutes_reference}</p>}
                        <div className="ais-form-row">
                            <label>{t('Select the top')}<input type="number" min="0" value={topN} onChange={(e) => setTopN(e.target.value)} /></label>
                            <button type="button" className="ais-btn" onClick={pickTop}>{t('Select')}</button>
                        </div>
                        <div className="ais-form-row">
                            <label>{t('Or select everyone who meets and scores at least')}<input type="number" min="0" max="100" value={minScore} onChange={(e) => setMinScore(e.target.value)} /></label>
                            <button type="button" className="ais-btn" disabled={minScore === ''} onClick={pickMin}>{t('Select')}{reachMin !== null ? ` (${reachMin})` : ''}</button>
                        </div>
                        <p className="ais-small">{picked.length} {t('selected')} · {eligible.length} {t('meet every mandatory requirement')} · {data.candidates.length} {t('approved')}</p>
                        <div className="ais-table-wrap">
                            <table className="ais-table">
                                <thead><tr><th></th><th>{t('Application')}</th><th>{t('Score /100')}</th><th>{t('Committee')}</th><th>{t('Status')}</th></tr></thead>
                                <tbody>
                                    {data.candidates.map((c) => {
                                        const id = Number(c.application_id);
                                        const ok = c.eligibility === 'MEETS_MANDATORY';
                                        return (
                                            <tr key={id} className={ok ? '' : 'ais-row-muted'}>
                                                <td><input type="checkbox" disabled={!ok} checked={picked.includes(id)} onChange={(e) => setPicked(e.target.checked ? [...picked, id] : picked.filter((x) => x !== id))} aria-label={c.applicant_name} /></td>
                                                <td><b>{c.applicant_name}</b><div className="ais-small">{c.application_number}</div></td>
                                                <td><b>{Number(c.score)}</b>{Number(c.unscored_criteria) > 0 && <span className="ais-small"> · {c.unscored_criteria} {t('unscored')}</span>}</td>
                                                <td><span className={badgeClass(c.eligibility)}>{t(STATUS_LABELS[c.eligibility] || c.eligibility)}</span></td>
                                                <td><ShortlistBadge t={t} status={c.application_status} /></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div className="ais-review ais-sticky-actions">
                            <div className="ais-form-row">
                                <label>{t('Committee minutes reference')}<input value={minutes} onChange={(e) => setMinutes(e.target.value)} /></label>
                                <label>{t('Note (internal)')}<input value={note} onChange={(e) => setNote(e.target.value)} /></label>
                            </div>
                            <button type="button" className="ais-btn ais-btn-dark" disabled={busy || !picked.length || !minutes.trim()} onClick={save}>{t('Record shortlist')}</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// Applicants per score group. One measure across ordered groups: horizontal bars, one hue, values labelled at the bar end.
// Clicking a group filters the list below to it; clicking it again clears the filter.
// Degree field tick list: counts follow the other filters; the ticked fields filter the list and the reports.
const FieldFilter = ({ t, sw, runId, filters, onFilter }) => {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const chosen = (filters.fields || '').split(',').filter(Boolean);
    const { fields, page, ...others } = filters;
    const key = JSON.stringify(others);
    useEffect(() => {
        let live = true;
        const timer = setTimeout(() => {
            api.aiFieldCounts(runId, { ...others, lang: sw ? 'sw' : 'en' }).then((r) => live && setOptions(r.data || [])).catch(() => {});
        }, 400);
        return () => { live = false; clearTimeout(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runId, key, sw]);
    const set = (list) => onFilter({ fields: list.join(',') });
    const toggle = (k, on) => set(on ? [...chosen, k] : chosen.filter((x) => x !== k));
    return (
        <span className="ais-fieldfilter">
            <button type="button" className={`ais-btn${chosen.length ? ' ais-btn-dark' : ''}`} aria-expanded={open} onClick={() => setOpen(!open)}>
                {t('Degree field')}{chosen.length ? ` (${chosen.length})` : ''} ▾
            </button>
            {open && (
                <div className="ais-fieldfilter-panel" role="dialog" aria-label={t('Degree field')}>
                    <div className="ais-fieldfilter-head">
                        <b>{t('Show only these degree fields')}</b>
                        <button type="button" className="ais-link" onClick={() => setOpen(false)}>{t('Close')}</button>
                    </div>
                    {options.length === 0 && <p className="ais-small">{t('Loading...')}</p>}
                    {options.map((o) => (
                        <label key={o.key} className="ais-check">
                            <input type="checkbox" checked={chosen.includes(o.key)} onChange={(e) => toggle(o.key, e.target.checked)} />
                            <span>{o.label}</span><b className="ais-fieldfilter-n">{o.count}</b>
                        </label>
                    ))}
                    <div className="ais-actions">
                        <button type="button" className="ais-link" onClick={() => set(options.map((o) => o.key))}>{t('Tick all')}</button>
                        <button type="button" className="ais-link" onClick={() => set([])}>{t('Clear')}</button>
                        {chosen.length > 0 && <span className="ais-small">{options.filter((o) => chosen.includes(o.key)).reduce((a, o) => a + o.count, 0)} {t('applicants')}</span>}
                    </div>
                </div>
            )}
        </span>
    );
};

// Meets / needs review / does not meet at a glance (Committee status where reviewed, AI elsewhere). A tile filters the list.
const EligibilityStats = ({ t, counts, filters, onFilter }) => {
    if (!counts || !counts.total) return null;
    const tiles = [
        { key: 'MEETS_MANDATORY', n: counts.meets, ai: counts.ai_meets, cls: 'ais-stat-ok', label: 'Meets mandatory' },
        { key: 'NEEDS_REVIEW', n: counts.needs_review, ai: counts.ai_needs_review, cls: 'ais-stat-warn', label: 'Needs review' },
        { key: 'DOES_NOT_MEET', n: counts.does_not_meet, ai: counts.ai_does_not_meet, cls: 'ais-stat-bad', label: 'Does not meet' },
    ];
    const pick = (patch) => onFilter({ eligibility: '', approved: '', reviewed: '', status: '', ...patch });
    return (
        <section className="ais-stats" aria-label={t('Applicants by status')}>
            {tiles.map((x) => (
                <button key={x.key} type="button" className={`ais-stat ${x.cls}${filters.eligibility === x.key ? ' on' : ''}`} aria-pressed={filters.eligibility === x.key}
                    onClick={() => pick({ eligibility: filters.eligibility === x.key ? '' : x.key })}>
                    <small>{t(x.label)}</small>
                    <b>{x.n}</b>
                    <span>{pct(x.n, counts.total)}% · {t('AI')}: {x.ai}</span>
                </button>
            ))}
            <button type="button" className={`ais-stat${filters.reviewed === 'yes' ? ' on' : ''}`} aria-pressed={filters.reviewed === 'yes'} onClick={() => pick({ reviewed: filters.reviewed === 'yes' ? '' : 'yes' })}>
                <small>{t('Reviewed by the Committee')}</small><b>{counts.reviewed}</b><span>/ {counts.total} · {pct(counts.reviewed, counts.total)}%</span>
            </button>
            <button type="button" className={`ais-stat${filters.approved ? ' on' : ''}`} aria-pressed={!!filters.approved} onClick={() => pick({ approved: filters.approved ? '' : '1' })}>
                <small>{t('Committee approved')}</small><b>{counts.approved}</b><span>/ {counts.total} · {pct(counts.approved, counts.total)}%</span>
            </button>
        </section>
    );
};

const ScoreBands = ({ t, bands, filters, onFilter }) => {
    const total = bands.reduce((sum, b) => sum + b.count, 0);
    if (!total) return null;
    const max = Math.max(...bands.map((b) => b.count), 1);
    const isActive = (b) => String(filters.min_score) === String(b.min) && String(filters.max_score) === String(b.max);
    return (
        <section className="ais-bands" aria-label={t('Applicants by score group')}>
            <div className="ais-bands-head">
                <b>{t('Applicants by score group')}</b>
                <span className="ais-small">{total} {t('assessed')} · {t('score /100')}</span>
            </div>
            <ul>
                {bands.map((b) => {
                    const active = isActive(b);
                    const tip = `${b.count} ${t('applicants')} · ${b.label} · ${b.meets} ${t('meet all mandatory requirements')}`;
                    return (
                        <li key={b.label}>
                            <button type="button" className={`ais-band ${active ? 'on' : ''}`} title={tip} aria-label={tip} aria-pressed={active}
                                onClick={() => onFilter(active ? { min_score: '', max_score: '' } : { min_score: b.min, max_score: b.max })}>
                                <span className="ais-band-label">{b.label}</span>
                                <span className="ais-band-track">
                                    <span className="ais-band-bar" style={{ width: `${b.count ? Math.max(2, (100 * b.count) / max) : 0}%` }} />
                                    <span className="ais-band-value"><b>{b.count}</b>{b.count > 0 && <small> · {b.meets} {t('meet mandatory')}</small>}</span>
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>
            <p className="ais-small">{t('Click a group to list those applicants. "Meet mandatory" = all mandatory requirements met (Committee decision where reviewed, otherwise AI proposal).')}</p>
        </section>
    );
};

const RunView = ({ t, sw, run, rows, pagination, filters, canManage, isAdmin, showOutcomeTabs, isReportApprover, outcomeSummary, onApproveAll, onApplicantPdf, onContactsPdf, busy, assignment, me, onAssign, onShortlist, onCutoff, onBack, onFilter, onPage, onOpen, onAction, onExport, onReport }) => {
    const mine = (assignment?.by_reviewer || []).find((r) => r.reviewer_id === me);
    const working = ['pending', 'running'].includes(run.status);
    const committeeActions = canManage && (isAdmin || !isReportApprover);
    return (
        <section className="ais-run">
            <div className="ais-run-head">
                <button type="button" className="ais-link" onClick={onBack}>← {t('All posts')}</button>
                <h2>{t('Run')} {run.id} <span className={badgeClass(run.status)}>{t(RUN_LABELS[run.status] || run.status)}</span>{Number(run.is_pilot) ? <span className="ais-badge ais-pilot-badge">{t('Pilot – not for decisions')}</span> : null}</h2>
                <p className="ais-small">{run.model} · {run.prompt_version} · {t('tokens')} {Number(run.tokens_in).toLocaleString()} {t('in')} / {Number(run.tokens_out).toLocaleString()} {t('out')}</p>
                <div className="ais-progress ais-progress-lg"><div style={{ width: `${pct(Number(run.processed) + Number(run.failed), run.total)}%` }} /></div>
                <p className="ais-small">
                    {run.processed} {t('assessed')} · {run.failed} {t('failed')} · {run.needs_review} {t('for committee attention')} · {run.total} {t('total')}
                    {(run.errors || []).length > 0 && <> · {t('Errors')}: {run.errors.map((e) => `${e.error_code} (${e.n})`).join(', ')}</>}
                </p>
                <div className="ais-actions">
                    {canManage && working && <button type="button" className="ais-btn" disabled={busy} onClick={() => onAction('pause')}>{t('Pause')}</button>}
                    {canManage && run.status === 'paused' && <button type="button" className="ais-btn" disabled={busy} onClick={() => onAction('resume')}>{t('Resume')}</button>}
                    {canManage && working && <button type="button" className="ais-btn" disabled={busy} onClick={() => onAction('start_workers')}>{t('Restart workers')}</button>}
                    {canManage && Number(run.failed) > 0 && <button type="button" className="ais-btn" disabled={busy} onClick={() => onAction('retry_failed')}>{t('Retry failed')}</button>}
                    {canManage && (working || run.status === 'paused') && <button type="button" className="ais-btn ais-btn-danger" disabled={busy} onClick={() => window.confirm(t('Cancel this run? Assessed applications are kept.')) && onAction('cancel')}>{t('Cancel run')}</button>}
                    <button type="button" className="ais-btn" disabled={busy} onClick={() => onReport('xlsx', 'list')} title={t('One sheet: the applicant list only. Uses the filters below.')}>{t('Excel (list)')}</button>
                    <button type="button" className="ais-btn" disabled={busy} onClick={() => onReport('xlsx')} title={t('Uses the filters below')}>{t('Report: Excel')}</button>
                    <button type="button" className="ais-btn" disabled={busy} onClick={() => onReport('pdf')} title={t('Uses the filters below')}>{t('Report: PDF')}</button>
                    {committeeActions && <button type="button" className="ais-btn" onClick={onAssign}>{t('Assign to members')}</button>}
                    {committeeActions && <button type="button" className="ais-btn ais-btn-dark" onClick={onShortlist}>{t('Record shortlist')}</button>}
                    {onCutoff && <button type="button" className="ais-btn ais-btn-danger" onClick={onCutoff}>{t('Score cut-off…')}</button>}
                </div>
            </div>

            {(run.errors || []).some((e) => e.error_code === 'billing') && (
                <div className="ais-alert ais-alert-error">{t('The AI account has run out of credit. Add credit in the Anthropic Console (Plans & Billing), then press "Resume" or "Retry failed". Nothing is lost: finished assessments are kept.')}</div>
            )}

            {assignment && assignment.total > 0 && (
                <p className="ais-small ais-assign-line">
                    {t('Reviewed by the Committee')}: <b>{assignment.reviewed}</b> / {assignment.total}
                    {' · '}{t('Assigned')}: {assignment.assigned_applications}{assignment.unassigned ? ` · ${assignment.unassigned} ${t('not assigned')}` : ''}
                    {mine && <> · {t('Yours')}: <b>{mine.reviewed}</b> / {mine.assigned} {t('reviewed')}</>}
                </p>
            )}
            <EligibilityStats t={t} counts={run.eligibility_counts} filters={filters} onFilter={onFilter} />
            <ScoreBands t={t} bands={run.score_bands || []} filters={filters} onFilter={onFilter} />

            {showOutcomeTabs && <div className="ais-app-outcome-tabs" role="tablist" aria-label={t('Application outcome tabs')}>
                <div className="ais-gr-applicant-tabs">
                {[
                    ['', 'All applications'],
                    ['shortlisted', 'Shortlisted applicants'],
                    ['not_shortlisted', 'Applicants not shortlisted'],
                ].map(([outcome, label]) => <button key={outcome} type="button" role="tab"
                    aria-selected={(filters.application_outcome || '') === outcome}
                    className={`ais-btn${(filters.application_outcome || '') === outcome ? ' ais-btn-dark' : ''}`}
                    onClick={() => onFilter({
                        application_outcome: outcome, work_stage: '', mine: '', status: '', reviewed: '',
                        eligibility: '', approved: '', min_score: '', max_score: '', high_not_meet: '', fields: '', search: '', application_review: '',
                    })}>
                    {t(label)}{(filters.application_outcome || '') === outcome && ` (${pagination?.total || 0})`}
                </button>)}
                </div>
                <button type="button" className="ais-btn" disabled={busy} onClick={onApplicantPdf}>{t('Download applicant lists')}</button>
                <button type="button" className="ais-btn" disabled={busy} onClick={onContactsPdf}>{t('Shortlisted contacts (PDF)')}</button>
                {isReportApprover && outcomeSummary?.ready && outcomeSummary.pending > 0 && <button type="button" className="ais-btn ais-btn-ok" disabled={busy} onClick={onApproveAll}>
                    {t('Accept all pending outcomes')} ({outcomeSummary.pending})
                </button>}
                {isReportApprover && outcomeSummary && !outcomeSummary.ready && <p className="ais-alert ais-alert-warn ais-outcome-readiness">{t(outcomeSummary.reason || 'Secretary General review is not available yet.')}</p>}
                {isReportApprover && outcomeSummary?.ready && <p className="ais-small ais-outcome-readiness">
                    {t('Secretary General review progress')}: {outcomeSummary.approved}/{outcomeSummary.total} · {outcomeSummary.pending} {t('pending')}
                    {outcomeSummary.returned > 0 && <> · {outcomeSummary.returned} {t('returned to Committee')}</>}
                    {' · '}{outcomeSummary.shortlisted} {t('shortlisted')} / {outcomeSummary.not_shortlisted} {t('not shortlisted')}
                </p>}
            </div>}

            <div className="ais-filters">
                <select value={filters.work_stage || ''} onChange={(e) => onFilter({ work_stage: e.target.value, mine: e.target.value === 'second' ? '1' : filters.mine, status: '', reviewed: '', eligibility: '', approved: '', min_score: '', max_score: '', high_not_meet: '', fields: '', search: '' })} aria-label={t('Review work list')}>
                    <option value="">{t('All applications / history')}</option>
                    <option value="second">{t('My second reviews — ready now')}</option>
                </select>
                {mine && (
                    <label className="ais-check ais-mine"><input type="checkbox" checked={!!filters.mine} onChange={(e) => onFilter({ mine: e.target.checked ? '1' : '' })} /> {t('My applications only')} ({mine.assigned})</label>
                )}
                <select value={filters.status} onChange={(e) => onFilter({ status: e.target.value })} aria-label={t('Status')}>
                    <option value="">{t('All statuses')}</option>
                    {['MEETS_MANDATORY', 'NEEDS_REVIEW', 'DOES_NOT_MEET', 'COMMITTEE_APPROVED'].map((s) => <option key={s} value={s}>{t(STATUS_LABELS[s])}</option>)}
                </select>
                <select value={filters.application_review || ''} onChange={(e) => onFilter({ application_review: e.target.value })} aria-label={t('Secretary General outcome review')}>
                    <option value="">{t('All Secretary General review statuses')}</option>
                    <option value="pending">{t('Pending Secretary General review')}</option>
                    <option value="approved">{t('Accepted by Secretary General')}</option>
                    <option value="returned">{t('Returned to Committee')}</option>
                </select>
                <select value={filters.reviewed} onChange={(e) => onFilter({ reviewed: e.target.value })} aria-label={t('Reviewed')}>
                    <option value="">{t('Reviewed or not')}</option>
                    <option value="no">{t('Not yet reviewed')}</option>
                    <option value="yes">{t('Reviewed')}</option>
                </select>
                <FieldFilter t={t} sw={sw} runId={run.id} filters={filters} onFilter={onFilter} />
                <span className="ais-score-range">
                    <label>{t('Score from')}<input type="number" min="0" max="100" placeholder="0" value={filters.min_score} onChange={(e) => onFilter({ min_score: e.target.value })} /></label>
                    <label>{t('to')}<input type="number" min="0" max="100" placeholder="100" value={filters.max_score} onChange={(e) => onFilter({ max_score: e.target.value })} /></label>
                    {(filters.min_score !== '' || filters.max_score !== '') && <button type="button" className="ais-link" onClick={() => onFilter({ min_score: '', max_score: '' })}>{t('Clear')}</button>}
                </span>
                <input type="search" placeholder={t('Name or application number')} value={filters.search} onChange={(e) => onFilter({ search: e.target.value })} />
                <label className="ais-check ais-mine"><input type="checkbox" checked={!!filters.high_not_meet} onChange={(e) => onFilter({ high_not_meet: e.target.checked ? '1' : '' })} /> {t('High score (80+) but does not meet')}</label>
                <select value={`${filters.sort}:${filters.dir}`} onChange={(e) => { const [sort, dir] = e.target.value.split(':'); onFilter({ sort, dir }); }} aria-label={t('Sort')}>
                    <option value="eligible:desc">{t('Meets first, then highest score')}</option>
                    <option value="score:desc">{t('Highest score first')}</option>
                    <option value="score:asc">{t('Lowest score first')}</option>
                    <option value="unscored:desc">{t('Most unscored first')}</option>
                    <option value="application:asc">{t('Application number')}</option>
                </select>
            </div>

            {filters.work_stage === 'second' && <p className="ais-alert ais-alert-info">{t('Only applications awaiting your second review now. Completed reviews and applications waiting for first-review corrections are hidden.')} · <b>{pagination?.total || 0}</b></p>}
            <div className="ais-table-wrap">
                <table className="ais-table">
                    <thead>
                        <tr><th>{t('Application')}</th><th>{t('AI proposal')}</th><th>{t('Committee')}</th><th>{t('Score /100')}</th><th>{t('Unscored')}</th><th>{t('Reviewers')}</th><th>{t('Shortlist')}</th></tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} onClick={() => onOpen(r.id)} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onOpen(r.id)}>
                                <td><b>{r.applicant_name}</b><div className="ais-small">{r.application_number}</div></td>
                                <td><span className={badgeClass(r.eligibility_status)}>{t(STATUS_LABELS[r.eligibility_status] || r.eligibility_status)}</span></td>
                                <td><CommitteeStatus t={t} r={r} /></td>
                                <td><Score r={r} t={t} /></td>
                                <td>{Number(r.unscored_criteria) ? <span className="ais-badge ais-unscored">{r.unscored_criteria}</span> : '0'}</td>
                                <td className="ais-small">{r.reviewers || '—'}</td>
                                <td><ShortlistBadge t={t} status={r.application_status} /></td>
                            </tr>
                        ))}
                        {!rows.length && <tr><td colSpan="7" className="ais-empty">{working ? t('Assessments appear here as they finish.') : t('No assessments match.')}</td></tr>}
                    </tbody>
                </table>
            </div>

            {/* Phones: one card per applicant instead of the table. */}
            <ul className="ais-cards">
                {rows.map((r) => (
                    <li key={r.id}>
                        <button type="button" className="ais-card-item" onClick={() => onOpen(r.id)}>
                            <span className="ais-card-score"><Score r={r} t={t} /><small>/100</small></span>
                            <span className="ais-card-text">
                                <b>{r.applicant_name}</b>
                                <small>{r.application_number}</small>
                                <span className="ais-card-badges">
                                    <span className={badgeClass(r.eligibility_status)}>{t('AI')}: {t(STATUS_LABELS[r.eligibility_status] || r.eligibility_status)}</span>
                                    <CommitteeStatus t={t} r={r} />
                                    {Number(r.unscored_criteria) > 0 && <span className="ais-badge ais-unscored">{r.unscored_criteria} {t('unscored')}</span>}
                                    <ShortlistBadge t={t} status={r.application_status} />
                                </span>
                                {r.reviewers && <small>{t('Reviewers')}: {r.reviewers}</small>}
                                {(r.flags || []).length > 0 && <small className="ais-card-flags">{(r.flags || []).map((f) => t(f.code.replace(/_/g, ' '))).join(' · ')}</small>}
                            </span>
                            <span className="ais-card-arrow" aria-hidden="true">›</span>
                        </button>
                    </li>
                ))}
                {!rows.length && <li className="ais-empty">{working ? t('Assessments appear here as they finish.') : t('No assessments match.')}</li>}
            </ul>
            {pagination.total_pages > 1 && (
                <div className="ais-pages">
                    <button type="button" className="ais-btn" disabled={pagination.page <= 1} onClick={() => onPage(pagination.page - 1)}>‹</button>
                    <span>{pagination.page} / {pagination.total_pages} · {pagination.total}</span>
                    <button type="button" className="ais-btn" disabled={pagination.page >= pagination.total_pages} onClick={() => onPage(pagination.page + 1)}>›</button>
                </div>
            )}
        </section>
    );
};

// Kiswahili text where the AI or the rubric provides it, English otherwise.
const pick = (sw, en, swText) => (sw && swText ? swText : en);

const readPref = (key, fallback) => { try { const v = localStorage.getItem(key); return v === null ? fallback : v === '1'; } catch (e) { return fallback; } };
const savePref = (key, on) => { try { localStorage.setItem(key, on ? '1' : '0'); } catch (e) { /* storage blocked */ } };

// A criterion a reviewer must look at: anything not clearly met with a quote, unscored points, or one being edited.
const needsDecision = (c) => {
    if (c.kind === 'eligibility') {
        if (c.verify_stage === 'shortlist') return false;
        return (c.reviewed_finding || c.finding) !== 'MET' || !(c.evidence || []).length;
    }
    return (c.reviewed_points ?? c.proposed_points) === null || (c.reviewed_points ?? c.proposed_points) === undefined;
};

const AssessmentModal = ({ t, sw, data, canManage, canReview, nav, onClose, onSaved, onReviewed, onError, onWithdrawn }) => {
    const [edits, setEdits] = useState({});
    const [rationale, setRationale] = useState('');
    const [conflict, setConflict] = useState(false);
    const [saving, setSaving] = useState(false);
    const [docText, setDocText] = useState(null);
    const [opening, setOpening] = useState(null);
    const [viewer, setViewer] = useState(null);
    // Committee requirements outside the rubric (e.g. experience under 2 years) ticked as not met; null = unchanged.
    const [reasonsEdit, setReasonsEdit] = useState(null);
    const savedReasons = data.committee_reasons || [];
    const reasonsNow = reasonsEdit ?? savedReasons;
    const reasonsChanged = reasonsEdit !== null && [...reasonsEdit].sort().join() !== [...savedReasons].sort().join();
    const reasonOptions = data.committee_reason_options || [];
    const reasonLabel = (code) => { const o = reasonOptions.find((x) => x.code === code); return o ? pick(sw, o.label, o.label_sw) : code; };
    const [focusOnly, setFocusOnly] = useState(() => readPref('ais_focus_only', true));
    const [autoNext, setAutoNext] = useState(() => readPref('ais_auto_next', true));
    const [expanded, setExpanded] = useState({});
    const [moving, setMoving] = useState(false);
    const [outcomeNote, setOutcomeNote] = useState('');
    // Downloaded originals, kept while this application is open so reopening (e.g. at another page) is instant.
    const files = useRef({});
    useEffect(() => () => Object.values(files.current).forEach((f) => URL.revokeObjectURL(f.url)), []);
    const locked = !!data.locked_at;
    const editable = !locked && canReview && (data.can_first_review || data.can_second_review);
    const docsByLabel = Object.fromEntries((data.documents || []).filter((d) => d.label).map((d) => [d.label, d]));
    const eligibility = data.criteria.filter((c) => c.kind === 'eligibility');
    const scored = data.criteria.filter((c) => c.kind === 'scored');
    const draft = Number(data.is_pilot) || data.criteria_status !== 'approved';

    const setEdit = (id, patch) => setEdits({ ...edits, [id]: { ...(edits[id] || {}), ...patch } });
    // Opens the original file in an in-page viewer (beside the criteria on wide screens), at the cited page for PDFs.
    const openOriginal = async (doc, page = null) => {
        if (opening) return;
        const base = { docId: doc.id, label: doc.label, name: `${data.application_number}-${doc.label}`, page };
        const cached = files.current[doc.id];
        if (cached) { setViewer({ ...base, ...cached, loading: false }); return; }
        setOpening(doc.id);
        setViewer({ ...base, loading: true });
        try {
            const raw = await api.aiOriginalBlob(data.id, doc.id);
            // The server sends e.g. "application/pdf; charset=UTF-8": keep the bare type so the viewer recognises it.
            const type = (raw.type || '').split(';')[0].trim().toLowerCase();
            const file = { url: URL.createObjectURL(new Blob([raw], { type })), type };
            files.current[doc.id] = file;
            setViewer((v) => (v && v.docId === doc.id ? { ...v, ...file, loading: false } : v));
        } catch (e) {
            setViewer(null);
            onError(e.message);
        } finally {
            setOpening(null);
        }
    };
    const closeViewer = () => setViewer(null);
    const originalButton = (doc, page = null) => (
        <button type="button" className="ais-link" disabled={!!opening} aria-busy={opening === doc.id} onClick={() => openOriginal(doc, page)}>
            {opening === doc.id ? <><span className="ais-spinner" aria-hidden="true" />{t('Opening…')}</> : (page ? `${t('Original')} · ${t('p.')} ${page}` : t('Original'))}
        </button>
    );
    const unsaved = Object.keys(edits).length > 0 || reasonsChanged || rationale.trim() !== '';
    const move = async (dir) => {
        if (!nav || moving) return;
        if (unsaved && !window.confirm(t('You have unsaved changes on this application. Leave without saving?'))) return;
        setMoving(true);
        try { await (dir > 0 ? nav.onNext() : nav.onPrev()); } finally { setMoving(false); }
    };
    // Overall status as it will be after this reviewer's choices (same rule as the server: any NOT_MET → does not meet;
    // an UNKNOWN on a screening-stage criterion → needs review; shortlist-stage checks count only when not met).
    const finalFinding = (c) => edits[c.criterion_id]?.reviewed_finding || c.reviewed_finding || c.finding;
    const blocking = eligibility.filter((c) => {
        const f = finalFinding(c);
        return f === 'NOT_MET' || (f === 'UNKNOWN' && (c.verify_stage || 'screening') === 'screening');
    });
    const statusAfter = blocking.some((c) => finalFinding(c) === 'NOT_MET') || reasonsNow.length ? 'DOES_NOT_MEET' : (blocking.length ? 'NEEDS_REVIEW' : 'MEETS_MANDATORY');
    const reasonRef = useRef(null);
    // A review must settle eligibility: "unknown" screening criteria left open would save as "needs review".
    const undecided = statusAfter === 'NEEDS_REVIEW' ? blocking.filter((c) => finalFinding(c) === 'UNKNOWN') : [];
    const markAllMet = () => {
        const names = [...blocking.map((c) => `• ${pick(sw, c.label, c.label_sw)}: ${t(STATUS_LABELS[finalFinding(c)])} → ${t('Met')}`),
            ...reasonsNow.map((r) => `• ${reasonLabel(r)}: ${t('Not met')} → ${t('Met')}`)].join('\n');
        if (!window.confirm(`${t('Change these mandatory criteria to "Met"? Check the original documents first.')}\n\n${names}`)) return;
        const next = { ...edits };
        blocking.forEach((c) => { next[c.criterion_id] = { ...(next[c.criterion_id] || {}), reviewed_finding: 'MET' }; });
        setEdits(next);
        if (reasonsNow.length) setReasonsEdit([]);
        setTimeout(() => { reasonRef.current?.focus(); reasonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 50);
    };
    // The other direction: the reviewer names which mandatory requirement is not met.
    const [failPick, setFailPick] = useState(null);
    const failCandidates = eligibility.filter((c) => finalFinding(c) !== 'NOT_MET');
    const reasonCandidates = reasonOptions.filter((o) => !reasonsNow.includes(o.code));
    const applyNotMet = () => {
        const chosen = failCandidates.filter((c) => failPick?.[c.criterion_id]);
        const chosenReasons = reasonCandidates.filter((o) => failPick?.[`r:${o.code}`]).map((o) => o.code);
        if (!chosen.length && !chosenReasons.length) return;
        const next = { ...edits };
        chosen.forEach((c) => { next[c.criterion_id] = { ...(next[c.criterion_id] || {}), reviewed_finding: 'NOT_MET' }; });
        setEdits(next);
        if (chosenReasons.length) setReasonsEdit([...reasonsNow, ...chosenReasons]);
        setFailPick(null);
        setTimeout(() => { reasonRef.current?.focus(); reasonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 50);
    };
    const undoFindingEdits = () => { setEdits(Object.fromEntries(Object.entries(edits).filter(([, v]) => !('reviewed_finding' in v)).map(([k, v]) => [k, v]))); setReasonsEdit(null); };
    const findingEdits = Object.values(edits).filter((v) => 'reviewed_finding' in v && v.reviewed_finding !== '').length + (reasonsChanged ? 1 : 0);
    // Saved Committee changes that differ from the AI; while the application is not yet approved they can be undone.
    const overridden = eligibility.filter((c) => c.reviewed_finding && c.reviewed_finding !== c.finding);
    const undoSaved = () => {
        const names = [...overridden.map((c) => `• ${pick(sw, c.label, c.label_sw)}: ${t(STATUS_LABELS[c.reviewed_finding])} → ${t(STATUS_LABELS[c.finding])}`),
            ...savedReasons.map((r) => `• ${reasonLabel(r)}: ${t('removed')}`)].join('\n');
        if (!window.confirm(`${t('Undo the saved changes and go back to the AI findings for these criteria?')}\n\n${names}`)) return;
        const next = { ...edits };
        overridden.forEach((c) => { next[c.criterion_id] = { ...(next[c.criterion_id] || {}), reviewed_finding: c.finding }; });
        setEdits(next);
        if (savedReasons.length) setReasonsEdit([]);
        setTimeout(() => { reasonRef.current?.focus(); reasonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 50);
    };
    const toggleFocus = (on) => { setFocusOnly(on); savePref('ais_focus_only', on); setExpanded({}); };
    const toggleAutoNext = (on) => { setAutoNext(on); savePref('ais_auto_next', on); };
    const collapsed = (c) => focusOnly && !needsDecision(c) && !expanded[c.id] && !edits[c.criterion_id];
    const allCriteria = data.criteria.filter((c) => !(c.kind === 'eligibility' && c.verify_stage === 'shortlist'));
    const toDecide = allCriteria.filter(needsDecision).length;
    const showText = async (doc, page) => {
        try {
            const res = await api.aiDocumentText(data.id, doc.id);
            setDocText({ ...res.data, focus: page });
        } catch (e) { onError(e.message); }
    };

    const submit = async (decision) => {
        setSaving(true);
        try {
            const criteria = Object.entries(edits).map(([criterion_id, v]) => ({ criterion_id: Number(criterion_id), ...v }));
            if (conflict && !window.confirm(t('Declare a conflict of interest? You will be withdrawn from this application and another member will review it.'))) return;
            const res = await api.aiReview(data.id, { decision, rationale, conflict_of_interest_declared: conflict, criteria, ...(reasonsEdit !== null ? { committee_reasons: reasonsEdit } : {}) });
            if (res.data?.withdrawn) { onWithdrawn(res.message); return; }
            setEdits({});
            setReasonsEdit(null);
            setRationale('');
            if (onReviewed) await onReviewed(res.data, autoNext && !!nav);
            else onSaved(res.data);
        } catch (e) { onError(e.message); } finally { setSaving(false); }
    };
    const approve = async (decision) => {
        setSaving(true);
        try {
            const criteria = Object.entries(edits).map(([criterion_id, v]) => ({ criterion_id: Number(criterion_id), ...v }));
            const res = await api.aiApprove(data.id, { decision, rationale, conflict_of_interest_declared: conflict, ...(decision === 'approve' ? { criteria, ...(reasonsEdit !== null ? { committee_reasons: reasonsEdit } : {}) } : {}) });
            setEdits({});
            setReasonsEdit(null);
            setRationale('');
            if (onReviewed) await onReviewed(res.data, autoNext && !!nav);
            else onSaved(res.data);
        } catch (e) { onError(e.message); } finally { setSaving(false); }
    };
    const reviewOutcome = async (decision) => {
        setSaving(true);
        try {
            const result = await api.aiApplicationOutcomeReview(data.id, decision, outcomeNote);
            onSaved({ ...data, secretary_general_review: result.data });
            setOutcomeNote('');
        } catch (e) { onError(e.message); } finally { setSaving(false); }
    };

    const Evidence = ({ c }) => (
        <ul className="ais-evidence">
            {(c.evidence || []).map((e, i) => {
                const doc = docsByLabel[e.doc];
                return (
                    <li key={i}>
                        <q>{e.quote}</q>
                        <span className="ais-small"> — {e.doc} {t('page')} {e.page}{e.note ? ` · ${e.note}` : ''}</span>
                        {doc && <>{originalButton(doc, e.page)}<button type="button" className="ais-link" onClick={() => showText(doc, e.page)}>{t('Text')}</button></>}
                    </li>
                );
            })}
        </ul>
    );

    return (
        <div className={`ais-modal-overlay${viewer ? ' ais-split' : ''}`} role="dialog" aria-modal="true" aria-label={data.applicant_name || data.application_number}>
            <div className="ais-modal">
                <div className="ais-modal-head">
                    <div>
                        <small>{data.application_number} · {data.job_title} · {t('criteria')} v{data.criteria_version}</small>
                        <h2>{data.applicant_name}</h2>
                    </div>
                    <div className="ais-nav">
                        {nav && (
                            <>
                                <button type="button" className="ais-btn" disabled={moving} onClick={() => move(-1)} aria-label={t('Previous application')}>←</button>
                                {nav.label && <span className="ais-small">{nav.label}</span>}
                                <button type="button" className="ais-btn ais-btn-dark" disabled={moving} onClick={() => move(1)}>{moving ? <span className="ais-spinner" aria-hidden="true" /> : null}{t('Next')} →</button>
                            </>
                        )}
                        <button type="button" className="ais-close" onClick={onClose} aria-label={t('Close')}>×</button>
                    </div>
                </div>
                {draft && <div className="ais-alert ais-alert-warn">{Number(data.is_pilot) ? t('Pilot run: for measuring accuracy only, not for decisions.') : t('Criteria not yet approved by the Committee: results are drafts.')}</div>}

                <div className="ais-summary">
                    <div><small>{t('AI proposal')}</small><span className={badgeClass(data.eligibility_status)}>{t(STATUS_LABELS[data.eligibility_status])}</span></div>
                    <div><small>{t('Reviewed')}</small>{data.reviewed_eligibility_status ? <span className={badgeClass(data.reviewed_eligibility_status)}>{t(STATUS_LABELS[data.reviewed_eligibility_status])}</span> : <span className="ais-small">{t('Not reviewed')}</span>}</div>
                    <div><small>{t('Score /100')}</small><b className={(data.reviewed_eligibility_status || data.eligibility_status) === 'DOES_NOT_MEET' ? 'ais-score-out' : ''}>{data.reviewed_score ?? data.proposed_score ?? '—'}</b>{Number(data.unscored_criteria) > 0 && <span className="ais-small"> · {data.unscored_criteria} {t('unscored')}</span>}{(data.reviewed_eligibility_status || data.eligibility_status) === 'DOES_NOT_MEET' && <span className="ais-small ais-score-note">{t('not used – does not meet a mandatory requirement')}</span>}</div>
                    <div><small>{t('Status')}</small><span className={badgeClass(data.status)}>{t(STATUS_LABELS[data.status] || data.status)}</span></div>
                </div>
                <div className="ais-actions"><button type="button" className="ais-btn" onClick={() => api.aiApplicantReport(data.id, sw ? 'sw' : 'en').catch((e) => onError(e.message))}>📄 {t('Written report (PDF)')}</button></div>
                <p className="ais-small">{t('Reviewers')}: {(data.assigned_reviewers || []).length ? data.assigned_reviewers.map((r) => r.name).join(', ') : t('not assigned')}{data.assigned_to_me ? ` · ${t('assigned to you')}` : ''}</p>
                {(data.secretary_general_review?.ready || data.secretary_general_review?.is_approver) && <section className="ais-review ais-sg-outcome-review">
                    <h3>{t('Secretary General outcome review')}</h3>
                    {data.secretary_general_review.ready
                        ? <p>{t(data.secretary_general_review.outcome === 'shortlisted' ? 'Committee outcome: shortlisted' : 'Committee outcome: not shortlisted')}
                            {' · '}{t(data.secretary_general_review.status === 'approved' ? 'Accepted by Secretary General' : data.secretary_general_review.status === 'returned' ? 'Returned to Committee' : 'Pending Secretary General review')}</p>
                        : <p className="ais-alert ais-alert-warn">{t(data.secretary_general_review.reason || 'Secretary General review is not available yet.')}</p>}
                    {data.secretary_general_review.decision_note && <p className="ais-alert ais-alert-warn">{t('Secretary General note')}: {data.secretary_general_review.decision_note}</p>}
                    {data.secretary_general_review.can_review && <>
                        <label>{t('Committee feedback (required when returning)')}
                            <textarea rows="3" maxLength="2000" value={outcomeNote} onChange={(e) => setOutcomeNote(e.target.value)} />
                        </label>
                        <div className="ais-actions">
                            <button type="button" className="ais-btn ais-btn-ok" disabled={saving} onClick={() => reviewOutcome('approve')}>{t('Accept applicant outcome')}</button>
                            <button type="button" className="ais-btn" disabled={saving || !outcomeNote.trim()} onClick={() => reviewOutcome('return')}>{t('Return to Committee')}</button>
                        </div>
                    </>}
                </section>}
                {(data.flags || []).length > 0 && (
                    <ul className="ais-flags">{data.flags.map((f, i) => { const note = pick(sw, f.note, f.note_sw); return <li key={i}><b>{t(f.code.replace(/_/g, ' '))}</b>{note ? `: ${note}` : ''}</li>; })}</ul>
                )}

                {editable && (
                    <div className="ais-overall">
                        <div>
                            <small>{t('Overall status with your decisions')}</small>
                            <span className={badgeClass(statusAfter)}>{t(STATUS_LABELS[statusAfter])}</span>
                            {statusAfter !== data.eligibility_status && <span className="ais-small">{t('AI')}: {t(STATUS_LABELS[data.eligibility_status])}</span>}
                        </div>
                        {statusAfter !== 'MEETS_MANDATORY' && blocking.length > 0 && (
                            <button type="button" className="ais-btn ais-btn-ok" onClick={markAllMet}>{t('Change to: Meets mandatory requirements')}</button>
                        )}
                        {reasonsNow.length > 0 && (
                            <p className="ais-small ais-overall-hint ais-reason-chips">{reasonsNow.map((r) => <span key={r} className="ais-badge ais-does_not_meet">{reasonLabel(r)}: {t('Not met')}</span>)}</p>
                        )}
                        {(failCandidates.length > 0 || reasonCandidates.length > 0) && !failPick && (
                            <button type="button" className="ais-btn ais-btn-danger" onClick={() => setFailPick({})}>{t('Change to: Does not meet…')}</button>
                        )}
                        {findingEdits > 0 && <button type="button" className="ais-link" onClick={undoFindingEdits}>{t('Undo')}</button>}
                        {failPick && (
                            <div className="ais-failpick">
                                <p className="ais-small"><b>{t('Which mandatory requirement is not met?')}</b> {t('Tick at least one, after checking the original documents.')}</p>
                                {failCandidates.map((c) => (
                                    <label key={c.criterion_id} className="ais-check">
                                        <input type="checkbox" checked={!!failPick[c.criterion_id]} onChange={(e) => setFailPick({ ...failPick, [c.criterion_id]: e.target.checked })} />
                                        {pick(sw, c.label, c.label_sw)} <span className="ais-small">({t(STATUS_LABELS[finalFinding(c)])})</span>
                                    </label>
                                ))}
                                {reasonCandidates.map((o) => (
                                    <label key={o.code} className="ais-check ais-committee-reason">
                                        <input type="checkbox" checked={!!failPick[`r:${o.code}`]} onChange={(e) => setFailPick({ ...failPick, [`r:${o.code}`]: e.target.checked })} />
                                        {pick(sw, o.label, o.label_sw)}
                                        {o.months !== null && o.months !== undefined && <span className="ais-small"> ({t('experience counted')}: {o.months} {t('months')}{o.months >= 24 ? ` – ${t('2 years or more')}` : ''})</span>}
                                    </label>
                                ))}
                                <div className="ais-actions">
                                    <button type="button" className="ais-btn ais-btn-danger" disabled={!Object.values(failPick).some(Boolean)} onClick={applyNotMet}>{t('Mark as not met')}</button>
                                    <button type="button" className="ais-link" onClick={() => setFailPick(null)}>{t('Cancel')}</button>
                                </div>
                            </div>
                        )}
                        {findingEdits === 0 && (overridden.length > 0 || savedReasons.length > 0) && (
                            <button type="button" className="ais-btn" onClick={undoSaved}>↺ {t('Undo saved change (back to AI findings)')}</button>
                        )}
                        {findingEdits > 0 && <p className="ais-small ais-overall-hint">{t('Write the reason below (which document shows each requirement is met), then save.')}</p>}
                    </div>
                )}

                <label className="ais-check ais-focus-toggle">
                    <input type="checkbox" checked={focusOnly} onChange={(e) => toggleFocus(e.target.checked)} />
                    {t('Show only what needs your decision')} <b>({toDecide} / {allCriteria.length})</b>
                </label>

                <h3>{t('Mandatory criteria')}</h3>
                {eligibility.map((c) => collapsed(c) ? (
                    <button key={c.id} type="button" className="ais-criterion ais-criterion-clear" onClick={() => setExpanded({ ...expanded, [c.id]: true })}>
                        <b>{pick(sw, c.label, c.label_sw)}</b>
                        <span className={badgeClass(c.reviewed_finding || c.finding)}>{t(STATUS_LABELS[c.reviewed_finding || c.finding])}</span>
                        {c.verify_stage === 'shortlist' && <span className="ais-small">{t('checked at shortlisting')}</span>}
                        <span className="ais-small ais-expand">{t('Show')} ▸</span>
                    </button>
                ) : (
                    <div key={c.id} className={`ais-criterion${needsDecision(c) ? ' ais-criterion-attn' : ''}`}>
                        <div className="ais-criterion-head">
                            <b>{pick(sw, c.label, c.label_sw)}</b>
                            <span className={badgeClass(c.reviewed_finding || c.finding)}>{t(STATUS_LABELS[c.reviewed_finding || c.finding])}</span>
                            {c.reviewed_finding && c.reviewed_finding !== c.finding && <span className="ais-small">{t('AI')}: {t(STATUS_LABELS[c.finding])}</span>}
                            {c.verify_stage === 'shortlist' && <span className="ais-small">{t('checked at shortlisting')}</span>}
                        </div>
                        <p className="ais-reason">{pick(sw, c.reason, c.reason_sw)}</p>
                        <Evidence c={c} />
                        {editable && (
                            <select value={edits[c.criterion_id]?.reviewed_finding ?? ''} onChange={(e) => setEdit(c.criterion_id, { reviewed_finding: e.target.value })} aria-label={t('Your finding')}>
                                <option value="">{t('Keep')}</option>
                                <option value="MET">{t('Met')}</option>
                                <option value="NOT_MET">{t('Not met')}</option>
                                <option value="UNKNOWN">{t('Unknown')}</option>
                            </select>
                        )}
                    </div>
                ))}

                <h3>{t('Scored criteria')}</h3>
                {scored.map((c) => collapsed(c) ? (
                    <button key={c.id} type="button" className="ais-criterion ais-criterion-clear" onClick={() => setExpanded({ ...expanded, [c.id]: true })}>
                        <b>{pick(sw, c.label, c.label_sw)}</b>
                        <span className="ais-points">{c.reviewed_points ?? c.proposed_points} / {Number(c.max_points)}</span>
                        <span className="ais-small ais-expand">{t('Show')} ▸</span>
                    </button>
                ) : (
                    <div key={c.id} className={`ais-criterion${needsDecision(c) ? ' ais-criterion-attn' : ''}`}>
                        <div className="ais-criterion-head">
                            <b>{pick(sw, c.label, c.label_sw)}</b>
                            <span className="ais-points">{c.reviewed_points ?? c.proposed_points ?? t('Unscored')} / {Number(c.max_points)}</span>
                            {c.reviewed_points !== null && Number(c.reviewed_points) !== Number(c.proposed_points) && <span className="ais-small">{t('AI')}: {c.proposed_points ?? t('Unscored')}</span>}
                        </div>
                        <p className="ais-reason">{pick(sw, c.reason, c.reason_sw)}</p>
                        <Evidence c={c} />
                        <details className="ais-anchors"><summary>{t('Scoring levels')}</summary><ol start="0">{c.anchors.map((a, i) => <li key={i}><b>{a.points}</b> — {pick(sw, a.when, a.when_sw)}</li>)}</ol></details>
                        {editable && (
                            <input type="number" min="0" max={c.max_points} step="0.5" placeholder={t('Your points')} value={edits[c.criterion_id]?.reviewed_points ?? ''} onChange={(e) => setEdit(c.criterion_id, { reviewed_points: e.target.value })} />
                        )}
                    </div>
                ))}

                <h3>{t('Documents')}</h3>
                <ul className="ais-docs">
                    {(data.documents || []).map((d) => (
                        <li key={d.id}>
                            <b>{d.label}</b> <span className="ais-small">{t(d.category)}{d.classified_as ? ` · ${t('looks like')} ${d.classified_as.replace(/_/g, ' ')}` : ''}{d.certified_copy_visible === 'yes' ? ` · ${t('certified')}` : ''} · {d.pages || '?'} {t('pages')} · {d.text_layer === 'none' ? t('scanned') : t('text')}</span>
                            {originalButton(d)}
                            <button type="button" className="ais-link" onClick={() => showText(d, 1)}>{t('Text')}</button>
                        </li>
                    ))}
                </ul>
                {docText && (
                    <div className="ais-doctext">
                        <div className="ais-criterion-head"><b>{docText.label} · {t('text as the AI received it (personal details removed)')}</b><button type="button" className="ais-link" onClick={() => setDocText(null)}>{t('Close')}</button></div>
                        {docText.pages.map((p) => (
                            <section key={p.page} className={p.page === docText.focus ? 'ais-focus' : ''}>
                                <small>{t('page')} {p.page} · {p.method === 'ocr' ? `OCR ${p.ocr_confidence ?? ''}%` : t('text layer')}</small>
                                <pre>{p.text || t('(blank)')}</pre>
                            </section>
                        ))}
                    </div>
                )}

                {viewer && (
                    <div className="ais-viewer-overlay" role="dialog" aria-modal="true" aria-label={viewer.label}>
                        <div className="ais-viewer">
                            <div className="ais-viewer-head">
                                <b>{viewer.label}{viewer.page && viewer.type === 'application/pdf' ? ` · ${t('page')} ${viewer.page}` : ''}</b>
                                <span>
                                    {viewer.url && <a className="ais-link" href={viewer.url} target="_blank" rel="noopener noreferrer">{t('Open in new tab')}</a>}
                                    {viewer.url && <a className="ais-link" href={viewer.url} download={viewer.name}>{t('Download')}</a>}
                                    <button type="button" className="ais-link" onClick={closeViewer}>{t('Close')}</button>
                                </span>
                            </div>
                            <div className="ais-viewer-body">
                                {viewer.loading && (
                                    <div className="ais-viewer-loading"><span className="ais-spinner ais-spinner-lg" aria-hidden="true" /><p>{t('Opening document… please wait')}</p></div>
                                )}
                                {!viewer.loading && /^image\//.test(viewer.type) && <img src={viewer.url} alt={viewer.label} />}
                                {!viewer.loading && viewer.type === 'application/pdf' && <iframe key={`${viewer.url}#${viewer.page || 1}`} src={viewer.page ? `${viewer.url}#page=${viewer.page}` : viewer.url} title={viewer.label} />}
                                {!viewer.loading && !/^(image\/|application\/pdf)/.test(viewer.type || '') && (
                                    <div className="ais-viewer-loading"><p>{t('This file type cannot be shown here. Use Download.')}</p></div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {(data.reviews || []).length > 0 && (
                    <>
                        <h3>{t('Committee history')}</h3>
                        <ul className="ais-history">{data.reviews.map((r) => <li key={r.id}><b>{t(r.approval_stage)} · {t(r.decision)}</b> <span className="ais-small">{r.reviewer} · {r.reviewed_at}</span><p>{r.rationale}</p></li>)}</ul>
                    </>
                )}

                {!locked && canReview && (editable || data.can_second_review) && (
                    <div className="ais-review">
                        <label>{t('Reason (required for any change, return or eligibility reversal)')}
                            <textarea ref={reasonRef} rows="3" value={rationale} onChange={(e) => setRationale(e.target.value)} />
                        </label>
                        <label className="ais-check"><input type="checkbox" checked={conflict} onChange={(e) => setConflict(e.target.checked)} /> {t('I have a conflict of interest with this applicant')}</label>
                        {undecided.length > 0 && (
                            <div className="ais-alert ais-alert-warn">
                                <b>{t('Decide these before saving (Met or Not met):')}</b>
                                <ul>{undecided.map((c) => <li key={c.criterion_id}>{pick(sw, c.label, c.label_sw)}</li>)}</ul>
                                <span className="ais-small">{t('Use the selection under each criterion, or "Return for another look" if you cannot decide.')}</span>
                            </div>
                        )}
                        {nav && <label className="ais-check"><input type="checkbox" checked={autoNext} onChange={(e) => toggleAutoNext(e.target.checked)} /> {t('After saving, open the next application')}</label>}
                        <div className="ais-actions">
                            {editable && data.can_first_review && <><button type="button" className="ais-btn ais-btn-dark" disabled={saving || undecided.length > 0} onClick={() => submit(Object.keys(edits).length || reasonsChanged ? 'override' : 'confirm')}>{Object.keys(edits).length || reasonsChanged ? t('Save review with changes') : t('Confirm AI proposal')}</button>
                            <button type="button" className="ais-btn" disabled={saving} onClick={() => submit('return')}>{t('Return for another look')}</button></>}
                            {data.can_second_review && conflict && <button type="button" className="ais-btn" disabled={saving} onClick={() => submit('return')}>{t('Declare conflict of interest')}</button>}
                            {data.can_second_review && !draft && (
                                <>
                                    <button type="button" className="ais-btn ais-btn-ok" disabled={saving || conflict || undecided.length > 0 || ((findingEdits || Object.keys(edits).length) && !rationale.trim())} onClick={() => approve('approve')}>{t(Object.keys(edits).length || reasonsChanged ? 'Save changes and complete second review' : 'Second approval')}</button>
                                    <button type="button" className="ais-btn" disabled={saving || conflict} onClick={() => approve('return')}>{t('Send back to reviewer')}</button>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const fieldList = (v) => (Array.isArray(v) ? v.join('\n') : v || '');

const CriteriaModal = ({ t, sw, data: initial, canManage, onClose, onApproved, onError }) => {
    const [data, setData] = useState(initial);
    const [pdfBusy, setPdfBusy] = useState(false);
    const downloadPdf = async () => {
        setPdfBusy(true);
        try { await api.aiCriteriaPdf(data.id, sw ? 'sw' : 'en'); } catch (e) { onError(t(e.message)); } finally { setPdfBusy(false); }
    };
    const [minutes, setMinutes] = useState(initial.minutes_reference || '');
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(false);
    const [edits, setEdits] = useState({ criteria: {}, committee_resolutions: {} });
    const [note, setNote] = useState('');
    const [savedMsg, setSavedMsg] = useState('');
    const r = data.rubric;
    const resolutions = r.committee_resolutions || {};
    const decisionsSw = r.committee_decisions_required_sw || {};

    const approve = async () => {
        setSaving(true);
        try {
            await api.aiApproveCriteria(data.id, minutes);
            onApproved();
        } catch (e) { onError(e.message); } finally { setSaving(false); }
    };
    // Edits are kept per criterion code; anything not touched keeps its current value.
    const ce = (code) => edits.criteria[code] || {};
    const setCe = (code, patch) => setEdits((cur) => ({ ...cur, criteria: { ...cur.criteria, [code]: { ...(cur.criteria[code] || {}), ...patch } } }));
    const anchorsOf = (c) => ce(c.code).anchors || c.anchors.map((a) => ({ points: a.points, when: a.when, when_sw: a.when_sw || '' }));
    const setAnchor = (c, i, patch) => setCe(c.code, { anchors: anchorsOf(c).map((a, j) => (j === i ? { ...a, ...patch } : a)) });
    const maxOf = (c) => Number(ce(c.code).max_points ?? c.max_points);
    const total = r.scored.reduce((sum, c) => sum + maxOf(c), 0);
    const startEdit = () => { setEdits({ criteria: {}, committee_resolutions: { ...resolutions } }); setEditing(true); setSavedMsg(''); };
    const save = async () => {
        setSaving(true);
        try {
            const res = await api.aiEditCriteria(data.id, { ...edits, note });
            setData(res.data);
            setMinutes('');
            setEditing(false);
            setNote('');
            setSavedMsg(t('Saved as draft version N. It needs two approvals again.').replace('N', res.data.version));
        } catch (e) { onError(e.message); } finally { setSaving(false); }
    };

    const advertised = (c) => pick(sw, c.advertised_text, c.advertised_text_sw);
    return (
        <div className="ais-modal-overlay" role="dialog" aria-modal="true" aria-label={r.title}>
            <div className="ais-modal">
                <div className="ais-modal-head">
                    <div><small>{r.reference} · v{data.version} · {t(data.status)}</small><h2>{t(r.title)}</h2></div>
                    <button type="button" className="ais-close" onClick={onClose} aria-label={t('Close')}>×</button>
                </div>
                {savedMsg && <div className="ais-alert ais-alert-ok">{savedMsg}</div>}
                <div className="ais-actions">
                    <button type="button" className="ais-btn" disabled={pdfBusy} onClick={downloadPdf}>
                        {pdfBusy ? <><span className="ais-spinner" aria-hidden="true" />{t('Preparing PDF…')}</> : <>📄 {t('Download criteria (PDF)')}</>}
                    </button>
                </div>
                {canManage && data.status !== 'retired' && !editing && (
                    <div className="ais-actions"><button type="button" className="ais-btn" onClick={startEdit}>✎ {t('Edit criteria')}</button>
                        <span className="ais-small">{t('Changes are saved as a new draft version; approvals start again.')}</span></div>
                )}

                <h3>{t('Mandatory criteria')}</h3>
                <ul className="ais-rubric">{r.eligibility.map((c) => {
                    const fieldRule = c.rule?.type === 'award_level_and_field';
                    return (
                        <li key={c.code}>
                            {editing ? (
                                <div className="ais-pair">
                                    <label><small>Kiswahili</small><input value={ce(c.code).label_sw ?? c.label_sw ?? ''} onChange={(e) => setCe(c.code, { label_sw: e.target.value })} /></label>
                                    <label><small>English</small><input value={ce(c.code).label ?? c.label} onChange={(e) => setCe(c.code, { label: e.target.value })} /></label>
                                </div>
                            ) : <b>{pick(sw, c.label, c.label_sw)}</b>}
                            {advertised(c) && <q>{advertised(c)}</q>}
                            <span className="ais-small">{c.verify_stage === 'shortlist' ? t('checked at shortlisting') : t('checked at screening')}{c.committee_decision ? ` · ${t('decision')} ${c.committee_decision}` : ''}</span>
                            {fieldRule && !editing && (
                                <div className="ais-small ais-fields">
                                    <div><b>{t('Named fields')}:</b> {(c.rule.named_fields || []).join(', ')}</div>
                                    <div><b>{t('Related fields accepted')}:</b> {(c.rule.related_fields_list || []).length ? c.rule.related_fields_list.join(', ') : t('none yet – such applicants are marked "Unknown" for review')}</div>
                                </div>
                            )}
                            {fieldRule && editing && (
                                <div className="ais-pair">
                                    <label><small>{t('Named fields (one per line)')}</small><textarea rows="4" value={ce(c.code).named_fields ?? fieldList(c.rule.named_fields)} onChange={(e) => setCe(c.code, { named_fields: e.target.value })} /></label>
                                    <label><small>{t('Related fields accepted (one per line)')}</small><textarea rows="4" value={ce(c.code).related_fields_list ?? fieldList(c.rule.related_fields_list)} onChange={(e) => setCe(c.code, { related_fields_list: e.target.value })} /></label>
                                </div>
                            )}
                        </li>
                    );
                })}</ul>

                <h3>{t('Scored criteria')} (<span className={editing && Math.abs(total - 100) > 0.001 ? 'ais-bad' : ''}>{total}</span> / 100 {t('points')})</h3>
                {editing && Math.abs(total - 100) > 0.001 && <div className="ais-alert ais-alert-warn">{t('The scored criteria must add up to 100 points.')}</div>}
                <ul className="ais-rubric">{r.scored.map((c) => (
                    <li key={c.code}>
                        {editing ? (
                            <>
                                <div className="ais-pair">
                                    <label><small>Kiswahili</small><input value={ce(c.code).label_sw ?? c.label_sw ?? ''} onChange={(e) => setCe(c.code, { label_sw: e.target.value })} /></label>
                                    <label><small>English</small><input value={ce(c.code).label ?? c.label} onChange={(e) => setCe(c.code, { label: e.target.value })} /></label>
                                </div>
                                <label className="ais-points-edit">{t('Maximum points')}
                                    <input type="number" min="1" max="100" step="0.5" value={ce(c.code).max_points ?? c.max_points} onChange={(e) => setCe(c.code, { max_points: e.target.value })} />
                                </label>
                                <ol start="0" className="ais-anchor-edit">{anchorsOf(c).map((a, i) => (
                                    <li key={i}>
                                        <input type="number" min="0" step="0.5" value={a.points} onChange={(e) => setAnchor(c, i, { points: e.target.value })} aria-label={t('Points')} />
                                        <textarea rows="2" value={a.when_sw} onChange={(e) => setAnchor(c, i, { when_sw: e.target.value })} aria-label="Kiswahili" placeholder="Kiswahili" />
                                        <textarea rows="2" value={a.when} disabled={c.rule?.type === 'duration_months'} onChange={(e) => setAnchor(c, i, { when: e.target.value })} aria-label="English" placeholder="English"
                                            title={c.rule?.type === 'duration_months' ? t('The English text of duration levels is fixed: the software reads the months from it.') : ''} />
                                    </li>
                                ))}</ol>
                            </>
                        ) : (
                            <><b>{pick(sw, c.label, c.label_sw)} — {c.max_points}</b>
                                {advertised(c) && <q>{advertised(c)}</q>}
                                <ol start="0">{c.anchors.map((a, i) => <li key={i}><b>{a.points}</b> — {pick(sw, a.when, a.when_sw)}</li>)}</ol></>
                        )}
                    </li>
                ))}</ul>
                {(sw ? r.not_scored_sw : r.not_scored)?.length > 0 && <p className="ais-small"><b>{t('Not scored from documents')}:</b> {(sw ? r.not_scored_sw : r.not_scored).join('; ')}</p>}
                {(sw ? r.not_checked_at_screening_sw : r.not_checked_at_screening)?.length > 0 && <p className="ais-small"><b>{t('Not required at this stage')}:</b> {(sw ? r.not_checked_at_screening_sw : r.not_checked_at_screening).join('; ')}</p>}

                <h3>{t('Decisions the Committee must take')}</h3>
                <dl className="ais-decisions">{Object.entries(r.committee_decisions_required || {}).map(([k, v]) => (
                    <React.Fragment key={k}>
                        <dt>{k}</dt>
                        <dd>
                            {pick(sw, v, decisionsSw[k])}
                            {editing ? (
                                <textarea rows="2" className="ais-resolution-input" placeholder={t('Committee decision (leave empty if still open)')}
                                    value={edits.committee_resolutions[k] ?? ''} onChange={(e) => setEdits((cur) => ({ ...cur, committee_resolutions: { ...cur.committee_resolutions, [k]: e.target.value } }))} />
                            ) : resolutions[k] ? <div className="ais-resolution"><b>{t('Decided')}:</b> {resolutions[k]}</div> : <div className="ais-small">{t('Open')}</div>}
                        </dd>
                    </React.Fragment>
                ))}</dl>

                {editing && (
                    <div className="ais-review ais-sticky-actions">
                        <label>{t('Reason for the change (kept in the history)')}<input value={note} onChange={(e) => setNote(e.target.value)} /></label>
                        <div className="ais-actions">
                            <button type="button" className="ais-btn ais-btn-dark" disabled={saving || Math.abs(total - 100) > 0.001} onClick={save}>{t('Save as new draft')}</button>
                            <button type="button" className="ais-btn" disabled={saving} onClick={() => setEditing(false)}>{t('Cancel')}</button>
                        </div>
                    </div>
                )}

                {!editing && data.status === 'draft' && (data.approvers || []).length > 0 && (
                    <p className="ais-small"><b>{t('Criteria approvers')}:</b> {data.approvers.map((a) => a.name).join(', ')}</p>
                )}
                {!editing && (canManage || (data.approvers || []).length > 0) && data.status === 'draft' && data.can_approve === false && (
                    <div className="ais-alert ais-alert-warn">{t('Only the two members the administrator designated can approve the criteria.')}</div>
                )}
                {!editing && data.status === 'draft' && data.can_approve === true && (
                    <div className="ais-review">
                        <p className="ais-small">{data.approved_by ? t('One approval recorded. A different committee member gives the second approval.') : t('Approval needs two different committee members. Approved criteria are frozen.')}</p>
                        <label>{t('Committee minutes reference')}<input value={minutes} onChange={(e) => setMinutes(e.target.value)} /></label>
                        <button type="button" className="ais-btn ais-btn-dark" disabled={saving || !minutes.trim()} onClick={approve}>{data.approved_by ? t('Give second approval') : t('Approve criteria')}</button>
                    </div>
                )}
            </div>
        </div>
    );
};

const OUTCOMES = [
    ['shortlisted', 'Shortlisted'],
    ['not_shortlisted', 'Not selected for shortlist'],
];

// Feedback shown to applicants on the careers tracking page, from Committee decisions only.
const FeedbackModal = ({ t, job, isAdmin, onClose }) => {
    const [state, setState] = useState(null);
    const [form, setForm] = useState(null);
    const [err, setErr] = useState('');
    const [msg, setMsg] = useState('');
    const [busy, setBusy] = useState(false);
    const [previewNo, setPreviewNo] = useState('');
    const [preview, setPreview] = useState(null);
    const [previewLang, setPreviewLang] = useState('sw');

    // formRef always holds the latest edits, so "Save" right after typing never sends a stale copy.
    const formRef = useRef(null);
    const update = (next) => { formRef.current = next; setForm(next); };
    const load = (data) => { setState(data); update(JSON.parse(JSON.stringify(data.settings))); };
    // Load once per post; `load` only sets local state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { api.aiFeedback(job.id).then((r) => load(r.data)).catch((e) => setErr(e.message)); }, [job.id]);

    const released = state?.release?.status === 'released';
    const run = async (fn, okText) => {
        setBusy(true); setErr(''); setMsg('');
        try { const r = await fn(); if (r?.data?.settings) load(r.data); setMsg(r?.message || okText); return r; }
        catch (e) { setErr(e.message); return null; }
        finally { setBusy(false); }
    };
    const save = () => run(() => api.aiSaveFeedback(job.id, formRef.current), t('Draft saved.'));
    const setHold = async (paused) => {
        const q = paused
            ? t('Pause applicant feedback for ALL posts? Released feedback will be hidden on the tracking page until it is allowed again.')
            : t('Allow applicant feedback? Released feedback becomes visible on the tracking page for ALL posts, and posts can be released. Make sure the Secretary General has accepted the outcomes and the shortlisted message is correct.');
        if (!window.confirm(q)) return;
        const r = await run(() => api.aiFeedbackHold(paused), paused ? t('Applicant feedback paused.') : t('Applicant feedback allowed.'));
        if (r) setState((s) => ({ ...s, notifications_paused: paused }));
    };
    const doPreview = async (number = previewNo) => {
        if (!String(number).trim()) return;
        setPreviewNo(number);
        if (!released) await save();
        try { setPreview((await api.aiPreviewFeedback(job.id, String(number).trim())).data); } catch (e) { setErr(e.message); setPreview(null); }
    };
    const release = async () => {
        const c = state.counts;
        if (!window.confirm(t('Release feedback to {n} applicants? They will see it on the tracking page.').replace('{n}', c.shortlisted + c.not_shortlisted + c.not_met))) return;
        await save();
        await run(() => api.aiFeedbackAction(job.id, 'release'));
    };
    const withdraw = () => window.confirm(t('Withdraw the feedback from the tracking page?')) && run(() => api.aiFeedbackAction(job.id, 'withdraw'));
    const sendTest = async () => {
        if (!previewNo.trim() || !window.confirm(t('Show this feedback, marked as a test, to application {n} only?').replace('{n}', previewNo.trim()))) return;
        if (!released) await save();
        await run(() => api.aiFeedbackAction(job.id, 'test_release', { application_number: previewNo.trim() }));
    };
    const removeTests = () => run(() => api.aiFeedbackAction(job.id, 'remove_tests'));
    const setPath = (path, value) => {
        const next = JSON.parse(JSON.stringify(formRef.current));
        let o = next;
        path.slice(0, -1).forEach((k) => { o[k] = o[k] || {}; o = o[k]; });
        o[path[path.length - 1]] = value;
        update(next);
    };
    const Pair = ({ path, rows = 3 }) => (
        <div className="ais-pair">
            {['sw', 'en'].map((lang) => (
                <label key={lang}><small>{lang === 'sw' ? 'Kiswahili' : 'English'}</small>
                    <textarea rows={rows} disabled={released} defaultValue={path.reduce((o, k) => (o ? o[k] : ''), form)?.[lang] ?? ''}
                        onBlur={(e) => setPath([...path, lang], e.target.value)} />
                </label>
            ))}
        </div>
    );

    return (
        <div className="ais-modal-overlay" role="dialog" aria-modal="true" aria-label={t('Feedback to applicants')}>
            <div className="ais-modal">
                <div className="ais-modal-head">
                    <div><small>{job.reference_number}</small><h2>{t('Feedback to applicants')}</h2></div>
                    <button type="button" className="ais-close" onClick={onClose} aria-label={t('Close')}>×</button>
                </div>
                {err && <div className="ais-alert ais-alert-error">{err}</div>}
                {msg && <div className="ais-alert ais-alert-ok">{t(msg)}</div>}
                {!state && !err && <div className="ais-empty">{t('Loading...')}</div>}
                {state && form && (
                    <>
                        {state.notifications_paused !== false && <div className="ais-alert ais-alert-warn">{t('Applicant notifications are paused. Approval does not release feedback. Drafting and internal previews remain available.')}
                            {isAdmin && <> <button type="button" className="ais-btn ais-btn-ok" disabled={busy} onClick={() => setHold(false)}>{t('Allow applicant feedback')}</button></>}</div>}
                        {state.notifications_paused === false && isAdmin && <div className="ais-alert ais-alert-ok">{t('Applicant feedback is allowed: released feedback is visible on the tracking page.')} <button type="button" className="ais-btn" disabled={busy} onClick={() => setHold(true)}>{t('Pause (hide all feedback)')}</button></div>}
                        <p className="ais-small">{t('Applicants see this on the careers tracking page after entering their application number and email or phone. It is built only from Committee-approved results and the shortlist recorded in Recruitment; it never shows scores, other applicants or AI text.')}</p>
                        <div className="ais-summary">
                            <div><small>{t('Shortlisted')}</small><b>{state.counts.shortlisted}</b></div>
                            <div><small>{t('Met, not shortlisted')}</small><b>{state.counts.not_shortlisted}</b></div>
                            <div><small>{t('Requirements not shown')}</small><b>{state.counts.not_met}</b></div>
                            <div><small>{t('Not ready (not approved)')}</small><b>{state.counts.not_ready}</b></div>
                        </div>
                        {!state.source_run && <div className="ais-alert ais-alert-warn">{t('No full screening run with approved criteria yet. You can prepare the wording now; release opens after the Committee approves results and records the shortlist.')}</div>}
                        {released && <div className="ais-alert ais-alert-ok">{t('Released on')} {state.release.released_at}. {t('Withdraw to change the wording.')}</div>}

                        <h3>{t('Messages')}</h3>
                        <div><b className="ais-small">{t('Shortlisted')}</b><Pair path={['intro', 'shortlisted']} /></div>
                        <div><b className="ais-small">{t('Not selected for shortlist')}</b><p className="ais-alert ais-alert-info">{form.intro.not_shortlisted.sw}<br />{form.intro.not_shortlisted.en}</p></div>

                        <h3>{t('Preview')}</h3>
                        <div className="ais-form-row">
                            <input placeholder={t('Application number')} value={previewNo} onChange={(e) => setPreviewNo(e.target.value)} />
                            <button type="button" className="ais-btn" disabled={busy} onClick={() => doPreview()}>{t('Show preview')}</button>
                        </div>
                        {Object.keys(state.preview_examples || {}).length > 0 && (
                            <div className="ais-row ais-examples">
                                <span className="ais-small">{t('Examples')}:</span>
                                {OUTCOMES.map(([k, label]) => {
                                    const example = k === 'not_shortlisted'
                                        ? state.preview_examples.not_shortlisted || state.preview_examples.not_met
                                        : state.preview_examples[k];
                                    return example && <button key={k} type="button" className="ais-btn" disabled={busy} onClick={() => doPreview(example)}>
                                        {t(label)} · {example}
                                    </button>;
                                })}
                            </div>
                        )}
                        {preview && (
                            <div className={`ais-preview ais-preview-${preview.outcome}`}>
                                {preview.pilot_preview && <div className="ais-alert ais-alert-warn">{t('Preview from pilot results (AI proposals, not approved by the Committee). Applicants cannot see this.')}</div>}
                                {preview.provisional && <div className="ais-alert ais-alert-warn"><b>{t('EARLY – the decision is not final yet.')}</b> {t('This application has no second approval, so this is what the applicant would see if the current review is approved. Applicants cannot see this.')}</div>}
                                <div className="ais-criterion-head"><b>{t(OUTCOMES.find(([k]) => k === preview.outcome)?.[1] || (preview.outcome === 'not_met' ? 'Not selected for shortlist' : preview.outcome))}</b>
                                    <button type="button" className="ais-link" onClick={() => setPreviewLang(previewLang === 'sw' ? 'en' : 'sw')}>{previewLang === 'sw' ? 'English' : 'Kiswahili'}</button></div>
                                <p>{previewLang === 'sw' ? preview.intro_sw : preview.intro_en}</p>
                                {preview.items.length > 0 && <ul>{preview.items.map((it, i) => <li key={i}>{previewLang === 'sw' ? it.sw : it.en}</li>)}</ul>}
                                {(previewLang === 'sw' ? preview.footer_sw : preview.footer_en) && <p className="ais-small">{previewLang === 'sw' ? preview.footer_sw : preview.footer_en}</p>}
                                {!preview.provisional && <button type="button" className="ais-btn" disabled={busy || state.notifications_paused !== false} onClick={sendTest}>{t('Send as a test to this application only')}</button>}
                            </div>
                        )}
                        {Object.keys(state.tests || {}).length > 0 && (
                            <div className="ais-alert ais-alert-warn">
                                <span>{t('Test feedback visible on the tracking page for')}: <b>{Object.keys(state.tests).join(', ')}</b></span>
                                <button type="button" className="ais-btn" disabled={busy} onClick={removeTests}>{t('Remove tests')}</button>
                            </div>
                        )}

                        <div className="ais-actions ais-sticky-actions">
                            {!released && <button type="button" className="ais-btn" disabled={busy} onClick={save}>{t('Save draft')}</button>}
                            {!released && <button type="button" className="ais-btn ais-btn-ok" disabled={busy || !state.source_run || state.notifications_paused !== false} onClick={release}>{t('Release to applicants')}</button>}
                            {released && <button type="button" className="ais-btn ais-btn-danger" disabled={busy} onClick={withdraw}>{t('Withdraw')}</button>}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const ACCESS_LEVELS = [
    ['hidden', 'Hidden'],
    ['view', 'Can see'],
    ['review', 'Can review'],
    ['manage', 'Can manage'],
];

// Admin: choose who sees AI Screening. Saved as per-person permissions (also visible in Users & Roles).
// General recruitment report (all posts): figures from the system, narrative by the chairperson and the secretary.
const GR_SECTIONS = [
    ['intro', '1. Introduction', 'Purpose of the exercise, the advertisement (dates, channels) and the posts.'],
    ['observations', '6. Observations', 'What the Committee noticed: common gaps in applications, document problems, lessons for future adverts.'],
    ['recommendations', '9. Recommendations and next steps', 'Interviews, timelines and what the Committee recommends.'],
];
const GeneralReportTable = ({ value, columns, lang, editable, onChange, t }) => {
    const rows = (value || '').split('\n').filter((line) => line.trim()).map((line) => {
        const cells = line.split('|');
        return columns.map((_, i) => cells[i] || '');
    });
    const update = (next) => onChange(next.map((row) => row.join('|')).join('\n'));
    return <>
        <div className="ais-table-wrap"><table className="ais-table ais-gr-edit-table">
            <thead><tr>{columns.map((column, i) => <th key={i} className={column.en === 'No.' ? 'ais-gr-number' : undefined}>{column[lang]}</th>)}{editable && <th>{t('Actions')}</th>}</tr></thead>
            <tbody>{rows.map((row, i) => <tr key={i}>
                {row.map((cell, j) => <td key={j} className={columns[j].en === 'No.' ? 'ais-gr-number' : undefined}>{editable ? <input aria-label={`${columns[j][lang]} ${i + 1}`} value={cell} onChange={(e) => update(rows.map((r, n) => n === i ? r.map((c, k) => k === j ? e.target.value.replace(/\|/g, ' ') : c) : r))} /> : cell || '—'}</td>)}
                {editable && <td><button type="button" className="ais-link" onClick={() => update(rows.filter((_, n) => n !== i))}>{t('Remove row')}</button></td>}
            </tr>)}</tbody>
        </table></div>
        {editable && <button type="button" className="ais-btn" onClick={() => update([...rows, columns.map((_, i) => i === 0 ? String(rows.length + 1) : '')])}>{t('Add row')}</button>}
    </>;
};
const GeneralReportModal = ({ t, sw, onClose }) => {
    const [data, setData] = useState(null);
    const [sections, setSections] = useState(null);
    const [err, setErr] = useState('');
    const [msg, setMsg] = useState('');
    const [decisionNote, setDecisionNote] = useState('');
    const [busy, setBusy] = useState(false);
    const load = useCallback(() => api.aiGeneralReport(sw ? 'sw' : 'en').then((r) => { setData(r.data); setSections(r.data.document.sections); }).catch((e) => setErr(e.message)), [sw]);
    useEffect(() => { load(); }, [load]);
    const final = data?.document?.status === 'final';
    const status = data?.document?.status;
    const pending = status === 'pending';
    const editable = false;
    const dirty = data && sections && JSON.stringify(sections) !== JSON.stringify(data.document.sections);
    const save = async () => {
        setBusy(true); setErr(''); setMsg('');
        try { const r = await api.aiSaveGeneralReport(sections); setData({ ...data, document: r.data.document }); setMsg(t('Saved.')); return true; } catch (e) { setErr(t(e.message)); return false; } finally { setBusy(false); }
    };
    const action = async (a) => {
        const prompts = {
            sign: 'Sign the current report text and figures with your uploaded signature?',
            submit: 'Submit this completed Committee report to the Secretary General for final review?',
            approve: 'Accept this Committee report as final? Its contents and figures will be frozen and signed.',
            return: 'Return this report to the Committee? Explain the requested corrections in the note below.',
            reopen: 'Reopen this accepted report and return it to the Committee? Explain why below.',
        };
        if (!window.confirm(t(prompts[a]) + (['sign', 'submit', 'approve'].includes(a) ? '\n\n' + t('Your uploaded signature will be added to this document.') : ''))) return;
        if (dirty && ['submit', 'sign'].includes(a) && !await save()) return;
        setBusy(true); setErr(''); setMsg('');
        try {
            const r = await api.aiGeneralReportAction(a, decisionNote);
            setData(r.data); setSections(r.data.document.sections); setDecisionNote('');
            setMsg(t(a === 'submit' ? 'Report submitted to the Secretary General.' : a === 'approve' ? 'The Committee report has been accepted and finalised.' : a === 'sign' ? 'Report signed.' : a === 'reopen' ? 'The report was returned to the Committee.' : 'The report was returned to the Committee.'));
        } catch (e) { setErr(t(e.message)); } finally { setBusy(false); }
    };
    const pdf = async (lang) => {
        setBusy(true); setErr('');
        try { if (dirty && editable && !await save()) return; await api.aiGeneralReportPdf(lang); } catch (e) { setErr(t(e.message)); } finally { setBusy(false); }
    };
    const s = data?.stats;
    const T = s?.totals;
    const pc = (a, b) => (b ? `${Math.round((100 * a) / b)}%` : '–');
    const title = (p) => t(p.title);
    const close = () => { if (!dirty || window.confirm(t('You have unsaved changes on this application. Leave without saving?'))) onClose(); };
    return (
        <div className="ais-modal-overlay" role="dialog" aria-modal="true" aria-label={t('General report')}>
            <div className="ais-modal">
                <div className="ais-modal-head">
                    <div><small>{t('Export only')}</small><h2>{t('General recruitment report')}</h2></div>
                    <button type="button" className="ais-close" onClick={close} aria-label={t('Close')}>×</button>
                </div>
                {err && <div className="ais-alert ais-alert-error">{err}</div>}
                {msg && <div className="ais-alert ais-alert-ok">{msg}</div>}
                {!data && !err && <div className="ais-empty">{t('Loading...')}</div>}
                {data && (
                    <>
                        <div className="ais-actions">
                            <button type="button" className="ais-btn ais-btn-dark" disabled={busy || !data.application_review_readiness?.ready} onClick={() => pdf('sw')}>📄 PDF – Kiswahili</button>
                            <button type="button" className="ais-btn" disabled={busy || !data.application_review_readiness?.ready} onClick={() => pdf('en')}>📄 PDF – English</button>
                            {editable && <button type="button" className="ais-btn ais-btn-ok" disabled={busy || !dirty} onClick={save}>{t('Save')}</button>}
                            {editable && <button type="button" className="ais-btn" disabled={busy || !data.my_signature?.has_signature} onClick={() => action('sign')}>{t('Sign report')}</button>}
                            {data.can_submit && !pending && !final && <button type="button" className="ais-btn ais-btn-dark" disabled={busy || !data.my_signature?.has_signature || !!dirty} onClick={() => action('submit')}>{t('Submit to Secretary General')}</button>}
                            {data.can_review && pending && <>
                                <button type="button" className="ais-btn ais-btn-ok" disabled={busy || !data.my_signature?.has_signature} onClick={() => action('approve')}>{t('Accept report')}</button>
                                <button type="button" className="ais-btn" disabled={busy || !decisionNote.trim()} onClick={() => action('return')}>{t('Return to Committee')}</button>
                            </>}
                            {data.can_review && final && <button type="button" className="ais-btn" disabled={busy || !decisionNote.trim()} onClick={() => action('reopen')}>{t('Reopen and return to Committee')}</button>}
                        </div>
                        <p className={`ais-alert ${data.application_review_readiness?.ready ? 'ais-alert-ok' : 'ais-alert-warn'}`}>
                            {data.application_review_readiness?.ready
                                ? t('Every application outcome has been accepted by the Secretary General. The General Recruitment Report is ready to export.')
                                : t('The General Recruitment Report can only be exported after every application outcome is accepted by the Secretary General.')}
                        </p>
                        {!data.application_review_readiness?.ready && <ul className="ais-gr-reasons">
                            {(data.application_review_readiness?.jobs || []).filter((job) => !job.ready || job.pending || job.returned || job.approved !== job.total).map((job) =>
                                <li key={job.job_posting_id}><b>{job.title}</b> — {job.reason ? t(job.reason) : `${job.approved}/${job.total} ${t('application outcomes accepted')}`}
                                    {job.pending > 0 && ` · ${job.pending} ${t('pending')}`}
                                    {job.returned > 0 && ` · ${job.returned} ${t('returned to Committee')}`}
                                    {job.missing_applications > 0 && ` · ${job.missing_applications} ${t('applications not in screening run')}`}
                                </li>
                            )}
                        </ul>}
                        <p className="ais-alert ais-alert-info">{t('The Committee records and submits the shortlist for Chairperson approval. The Secretary General then accepts or returns each shortlisted and not-shortlisted application outcome in its screening run. The General Recruitment Report has no approval controls and is used for export only.')}</p>
                        {data.document.template_version >= 2 && <p className="ais-small">{t('Check the exercise dates before finalising: the source cover says 20–25 October 2026, while the introduction and conclusion say 20–24 October 2026.')}</p>}
                        {(data.document.template_version >= 2 ? data.template.sections : GR_SECTIONS.map(([key, label]) => ({ key, title: { sw: t(label), en: label }, type: 'text' }))).map((definition) => {
                            const { key } = definition;
                            const put = (lang, value) => setSections((current) => ({ ...current, [key]: { ...current[key], [lang]: value } }));
                            return <section key={key} className="ais-gr-section">
                                <h3>{definition.title[sw ? 'sw' : 'en']}</h3>
                                <div className="ais-gr-langs">
                                    {['sw', 'en'].map((lang) => <div key={lang} className="ais-gr-language">
                                        <b>{lang === 'sw' ? 'Kiswahili' : 'English'}</b>
                                        {definition.type === 'table'
                                            ? <GeneralReportTable value={sections?.[key]?.[lang]} columns={definition.columns} lang={lang} editable={editable && !busy} onChange={(value) => put(lang, value)} t={t} />
                                            : editable ? <textarea aria-label={`${definition.title[lang]} (${lang})`} rows={key === 'report_title' ? 2 : 6} maxLength="8000" value={sections?.[key]?.[lang] || ''} disabled={busy} onChange={(e) => put(lang, e.target.value)} />
                                                : <p className="ais-gr-text">{sections?.[key]?.[lang] || '—'}</p>}
                                    </div>)}
                                </div>
                                {key === 'scope' && <div className="ais-table-wrap"><table className="ais-table ais-gr-table">
                                    <thead><tr><th>{t('Job post')}</th><th>{t('Applications')}</th><th>%</th><th>{t('Share of all applications')}</th></tr></thead>
                                    <tbody>{s.posts.map((p) => <tr key={p.job_id}><td>{title(p)}</td><td>{p.applications}</td><td>{pc(p.applications, T.applications)}</td>
                                        <td className="ais-gr-barcell"><span className="ais-gr-bar" style={{ width: pc(p.applications, T.applications) }} /></td></tr>)}
                                        <tr className="ais-gr-total"><td>{t('Total')}</td><td>{T.applications}</td><td>100%</td><td /></tr></tbody>
                                </table></div>}
                                {key === 'results' && <>
                                    <div className="ais-table-wrap"><table className="ais-table ais-gr-table">
                                        <thead><tr><th>{t('Particulars')}</th>{s.posts.map((p) => <th key={p.job_id}>{title(p)}</th>)}<th className="ais-gr-totalcol">{t('Total')}</th></tr></thead>
                                        <tbody>{[
                                            ['applications', 'Total applications received'], ['meets', 'Applications meeting mandatory requirements'],
                                            ['does_not_meet', 'Applications not meeting mandatory requirements'], ['shortlisted', 'Candidates shortlisted'],
                                            ['needs_review', 'Applications requiring further review / verification'], ['approved', 'Completed second reviews'],
                                        ].map(([metric, label]) => <tr key={metric}><td>{t(label)}</td>
                                            {s.posts.map((p) => <td key={p.job_id}>{metric === 'shortlisted' && !(p.shortlisted + p.not_shortlisted) ? '—' : <>{p[metric]} <small className="ais-gr-pct">{pc(p[metric], metric === 'applications' ? T.applications : p.applications)}</small></>}</td>)}
                                            <td className="ais-gr-totalcol">{metric === 'shortlisted' && !(T.shortlisted + T.not_shortlisted) ? '—' : <><b>{T[metric]}</b> <small className="ais-gr-pct">{pc(T[metric], T.applications)}</small></>}</td></tr>)}</tbody>
                                    </table></div>
                                    <h4 className="ais-gr-charttitle">{t("Outcome by position (share of the position's applications)")}</h4>
                                    <div className="ais-gr-chart">
                                        {[...s.posts.map((p) => ({ ...p, label: title(p) })), { ...T, label: t('All positions'), job_id: 'all', total: true }].map((p) => (
                                            <div key={p.job_id} className={`ais-gr-chartrow${p.total ? ' ais-gr-chartall' : ''}`}>
                                                <span className="ais-gr-chartlabel">{p.label}</span>
                                                <span className="ais-gr-stack" role="img" aria-label={`${p.label}: ${pc(p.meets, p.applications)} ${t('meet')}`}>
                                                    {[['meets', 'ais-gr-ok'], ['does_not_meet', 'ais-gr-bad'], ['needs_review', 'ais-gr-warn']].map(([k, cls]) => p[k] > 0 && (
                                                        <span key={k} className={cls} style={{ width: pc(p[k], p.applications) }} title={`${p[k]} (${pc(p[k], p.applications)})`}>{p[k] / Math.max(1, p.applications) >= 0.09 ? pc(p[k], p.applications) : ''}</span>
                                                    ))}
                                                </span>
                                                <span className="ais-gr-chartval">{pc(p.meets, p.applications)} {t('meet')}</span>
                                            </div>
                                        ))}
                                        <p className="ais-gr-legend"><span className="ais-gr-ok" /> {t('Meet mandatory requirements')} <span className="ais-gr-bad" /> {t('Do not meet')} <span className="ais-gr-warn" /> {t('Still under review')}</p>
                                    </div>
                                    <p className="ais-small">{t('Figures reflect current assessment outcomes, including proposals awaiting Committee review. Meeting mandatory requirements does not mean shortlisted. A dash means the shortlist has not been recorded.')}</p>
                                </>}
                            </section>;
                        })}
                        <h3>{t('Annex: Supporting assessment figures')}</h3>
                        <ul className="ais-gr-reasons">{s.posts.map((p) => <li key={p.job_id}><b>{title(p)}</b> <span className="ais-small">({p.does_not_meet} {t('did not meet')})</span><ul>{p.reasons.map((r, i) => <li key={i}>
                            <span className="ais-gr-reasonrow"><span>{sw ? r.sw : r.en}: <b>{r.n}</b> ({pc(r.n, p.does_not_meet)})</span><span className="ais-gr-barcell"><span className="ais-gr-bar ais-gr-bar-bad" style={{ width: pc(r.n, p.does_not_meet) }} /></span></span>
                        </li>)}</ul></li>)}</ul>
                        <div className="ais-actions ais-sticky-actions">
                            {editable && <button type="button" className="ais-btn ais-btn-ok" disabled={busy || !dirty} onClick={save}>{t('Save')}</button>}
                            <button type="button" className="ais-btn" disabled={busy} onClick={() => pdf(sw ? 'sw' : 'en')}>{t('Download PDF')}</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const AccessModal = ({ t, onClose }) => {
    const [users, setUsers] = useState(null);
    const [err, setErr] = useState('');
    const [saving, setSaving] = useState(null);
    const [query, setQuery] = useState('');
    const [onlyWithAccess, setOnlyWithAccess] = useState(false);
    const approverIds = (users || []).filter((u) => u.criteria_approver).map((u) => u.id);
    const reportApprover = (users || []).find((u) => u.report_approver)?.id || null;
    const toggleReportApprover = async (u) => {
        const next = u.report_approver ? null : u.id;
        setSaving(u.id); setErr('');
        try {
            await api.aiSaveReportApprover(next);
            setUsers((users || []).map((x) => ({ ...x, report_approver: next === x.id })));
        } catch (e) { setErr(t(e.message)); } finally { setSaving(null); }
    };
    const toggleApprover = async (u) => {
        const next = u.criteria_approver ? approverIds.filter((id) => id !== u.id) : [...approverIds, u.id];
        if (next.length > 2) { setErr(t('Choose at most two members to approve the criteria.')); return; }
        setSaving(u.id); setErr('');
        try {
            await api.aiSaveCriteriaApprovers(next);
            setUsers((users || []).map((x) => ({ ...x, criteria_approver: next.includes(x.id) })));
        } catch (e) { setErr(t(e.message)); } finally { setSaving(null); }
    };
    useEffect(() => { api.aiAccess().then((r) => setUsers(r.data)).catch((e) => setErr(e.message)); }, []);
    const q = query.trim().toLowerCase();
    const shown = (users || []).filter((u) => (!onlyWithAccess || u.level !== 'hidden')
        && (!q || `${u.name || ''} ${u.email} ${u.roles}`.toLowerCase().includes(q)));
    const change = async (u, level) => {
        setSaving(u.id);
        setErr('');
        try {
            const r = await api.aiSaveAccess(u.id, level);
            setUsers(r.data);
        } catch (e) { setErr(e.message); } finally { setSaving(null); }
    };
    return (
        <div className="ais-modal-overlay" role="dialog" aria-modal="true" aria-label={t('Who can see AI Screening')}>
            <div className="ais-modal ais-modal-sm ais-access-modal">
                <div className="ais-modal-head"><h2>{t('Who can see AI Screening')}</h2><button type="button" className="ais-close" onClick={onClose} aria-label={t('Close')}>×</button></div>
                <p className="ais-small">{t('Hidden: no menu and no access. Can see: results and documents. Can review: also change findings with a reason. Can manage: also start runs, settings and approvals.')}</p>
                {err && <div className="ais-alert ais-alert-error">{err}</div>}
                {!users && !err && <div className="ais-empty">{t('Loading...')}</div>}
                {users && (
                    <div className="ais-filters">
                        <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('Search by name or email')} aria-label={t('Search by name or email')} />
                        <label className="ais-check"><input type="checkbox" checked={onlyWithAccess} onChange={(e) => setOnlyWithAccess(e.target.checked)} /> {t('Only people with access')}</label>
                    </div>
                )}
                {users && <p className="ais-small">{t('Members who have no portal account yet: invite them first in Members (they set their own password from the SMS link), then give access here.')}</p>}
                {users && <p className="ais-small"><b>{t('Criteria approvers')}:</b> {approverIds.length ? (users || []).filter((u) => u.criteria_approver).map((u) => u.name || u.email).join(', ') : t('not chosen – any member who can manage may approve')} {t('(choose two Committee members, e.g. the chairperson and the secretary)')}</p>}
                {users && <p className="ais-small"><b>{t('Secretary General application-outcome reviewer')}:</b> {reportApprover ? (users || []).find((u) => u.id === reportApprover)?.name || (users || []).find((u) => u.id === reportApprover)?.email : t('Not designated')} · {t('Give this person AI Screening view access before assigning this role.')}</p>}
                <ul className="ais-access">
                    {shown.map((u) => (
                        <li key={u.id}>
                            <span className="ais-access-who">
                                <b>{u.name || u.email}</b>
                                <small>{u.name ? `${u.email} · ` : ''}{u.roles}{u.status !== 'active' ? ` · ${u.status}` : ''}</small>
                            </span>
                            {(u.protected || u.level !== 'hidden') && (
                                <label className="ais-check ais-approver" title={t('Gives one of the two criteria approvals')}>
                                    <input type="checkbox" checked={!!u.criteria_approver} disabled={saving === u.id} onChange={() => toggleApprover(u)} /> {t('Approves criteria')}
                                </label>
                            )}
                            {(u.protected || u.level !== 'hidden' || u.report_approver) && (
                                <label className="ais-check ais-approver" title={t('Reviews each shortlisted and not-shortlisted outcome; accepts it or returns it to the Committee')}>
                                    <input type="checkbox" checked={!!u.report_approver} disabled={saving === u.id} onChange={() => toggleReportApprover(u)} /> {t('Secretary General applicant outcome review')}
                                </label>
                            )}
                            {u.protected ? (
                                <span className="ais-badge ais-committee_approved">{t('Admin – always full access')}</span>
                            ) : (
                                <select value={u.level} disabled={saving === u.id} onChange={(e) => change(u, e.target.value)} aria-label={t('Access for') + ' ' + (u.name || u.email)}>
                                    {ACCESS_LEVELS.map(([v, label]) => <option key={v} value={v}>{t(label)}</option>)}
                                </select>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

const SettingsModal = ({ t, onClose, onSaved }) => {
    const [form, setForm] = useState(null);
    const [key, setKey] = useState('');
    const [err, setErr] = useState('');
    useEffect(() => { api.aiSettings().then((r) => setForm(r.data)).catch((e) => setErr(e.message)); }, []);
    const save = async (e) => {
        e.preventDefault();
        try {
            await api.aiSaveSettings({ provider: form.provider, model: form.model, effort: form.effort, anthropic_api_key: key, external_approved: form.external_approved ? 1 : 0, approval_reference: form.approval_reference });
            onSaved();
        } catch (e2) { setErr(e2.message); }
    };
    return (
        <div className="ais-modal-overlay" role="dialog" aria-modal="true" aria-label={t('AI screening settings')}>
            <form className="ais-modal ais-modal-sm" onSubmit={save}>
                <div className="ais-modal-head"><h2>{t('AI screening settings')}</h2><button type="button" className="ais-close" onClick={onClose} aria-label={t('Close')}>×</button></div>
                {err && <div className="ais-alert ais-alert-error">{err}</div>}
                {form && (
                    <div className="ais-form">
                        <label>{t('AI provider')}
                            <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value, model: '' })}>
                                <option value="anthropic">Anthropic Claude</option>
                                <option value="openai">{t('OpenAI (key from AI Assistant settings)')}</option>
                            </select>
                        </label>
                        <label>{t('Model')}<input value={form.model} placeholder={form.provider === 'openai' ? 'gpt-5' : 'claude-opus-5-5'} onChange={(e) => setForm({ ...form, model: e.target.value })} /></label>
                        {form.provider === 'anthropic' && (
                            <>
                                <label>{t('Effort')}
                                    <select value={form.effort} onChange={(e) => setForm({ ...form, effort: e.target.value })}>{['low', 'medium', 'high', 'xhigh', 'max'].map((x) => <option key={x}>{x}</option>)}</select>
                                </label>
                                <label>{t('Anthropic API key')} {form.api_key_set && <span className="ais-small">({t('saved; leave empty to keep')})</span>}<input type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} /></label>
                            </>
                        )}
                        <fieldset>
                            <legend>{t('Privacy approval (Committee decision A2)')}</legend>
                            <p className="ais-small">{t('Applicant text is sent to the AI provider only after the data-protection review and provider terms are approved. Personal details are removed first; certificate images never leave the server.')}</p>
                            <label className="ais-check"><input type="checkbox" checked={!!form.external_approved} onChange={(e) => setForm({ ...form, external_approved: e.target.checked })} /> {t('External AI processing has been approved')}</label>
                            <label>{t('Approval reference (minutes)')}<input value={form.approval_reference} onChange={(e) => setForm({ ...form, approval_reference: e.target.value })} /></label>
                        </fieldset>
                        <div className="ais-actions"><button type="submit" className="ais-btn ais-btn-dark">{t('Save')}</button><button type="button" className="ais-btn" onClick={onClose}>{t('Cancel')}</button></div>
                    </div>
                )}
            </form>
        </div>
    );
};

export default AiScreening;
